import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Popper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import BlockIcon from '@material-ui/icons/Block'
import CallMadeIcon from '@material-ui/icons/CallMade'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import Dayjs from 'dayjs'
import Papa from 'papaparse'
import React from 'react'
import ReactDOM from 'react-dom'

import {
  APPROVAL_SOURCE_OPTIONS,
  APT_CATEGORIES,
  addAptLine,
  bulkAddAptCategory,
  fetchAptCatalogue,
  addAptVendor,
  aptRequestAction,
  aptVendorAction,
  clarifyAptLine,
  createAptRequest,
  resendAptLine,
  deleteAptLine,
  fetchAptList,
  fetchAptRequest,
  fetchAptRequests,
  fetchAptVendors,
  reorderAptLines,
  saveAptApproval,
  updateAptLine
} from 'api/apartmentProcurement'

import TypedDeleteDialog from 'app/pages/Procurement/components/TypedDeleteDialog'
import PendingOrdersDialog from 'app/pages/Procurement/Hq/PendingOrdersDialog'

import { NEW_CATEGORY, NewCategoryMenuItem, useNewCategoryPrompt } from './NewCategory'
import RequestTracking from './RequestTracking'
import {
  aptCategoryNames,
  matchAptCategory,
  useAptCategories
} from './aptCategories'
import { STATUS_META, apiErrorText, money } from './constants'
import {
  downstreamOptionsFor,
  isStageValue,
  lineStage,
  lineStageText,
  requestStageText,
  stageOf
} from './requestStage'
import { drawerWidth } from 'app/config'
import { HeaderSlotContext, RoleContext } from './roleContext'
import {
  COL_BANDS,
  DISPATCH_OPTIONS,
  ITEM_TYPE_OPTIONS,
  MANAGE_VENDORS,
  NUM_FIELDS,
  PINNED_COLS,
  SHEET_COLS,
  cellDead,
  invoiceEditable,
  matchesFilter,
  toSheetRows
} from './sheetColumns'
import useColumnBands, {
  bandHeaderRow,
  mapBandedCells
} from './useColumnBands'
import useSegmentGroups, { groupEntries } from './useSegmentGroups'

// PO shown on the request grid (2026-08-05). Deliberately NOT added to the
// shared SHEET_COLS: the HQ daily sheet already carries PO in its own working
// columns, and putting it in the shared list would move it there for no reason
// and render it twice. Sits LAST here for the same reason — that is where the
// daily sheet has it, and the front of this grid is for what identifies the
// line. Read-only; minted server-side when the request is created.
const PO_COL = {
  tip: "The purchase order this line belongs to. One number per request, minted when the request is created, shared by every line in it.",
  k: 'po_number',
  label: 'PO',
  kind: 'calc',
  width: 100,
  filter: true
}
/* ⭐⭐ Two columns the At Apartment grid drops from the shared set (Satya,
 * 2026-09-02). SHEET_COLS itself is untouched — the HQ daily sheet keeps both,
 * the same way STATUS_META and the view presets diverge on this side only.
 *
 *   fulfilment_status (Dispatch) — MERGED INTO Status. "can we have the status
 *     and dispatch same? they both should represent the same thing": they sat
 *     side by side saying different things about one row, because Status was
 *     the request rolled up and Dispatch was the line. One column now shows the
 *     line's stage AND sets it.
 *   reference_link (File/Link) — moved INTO the Product cell as an arrow
 *     button. The PO export keeps its own File column, deliberately unchanged.
 */
const DROPPED_COLS = ['fulfilment_status', 'reference_link']

// Same key, new job, so the tooltip has to say the new one.
const STATUS_TIP =
  'Where this line actually is, in the tracking timeline\u2019s own words: requested \u2192 cluster approved \u2192 final approved \u2192 at Procurement HQ \u2192 sent to vendor \u2192 received by HQ \u2192 handed to At Apartment \u2192 dispatched \u2192 received. Click the chip for the full dated tracking; use the arrow beside it to move the line on.'

const PRODUCT_TIP =
  'What is being procured. Search this column by the item OR by the game it belongs to — typing “Tale trail” finds its Story Mat, Object cards and the rest, even though none of them carry the name.'

const GRID_COLS = [
  ...SHEET_COLS.filter((c) => !DROPPED_COLS.includes(c.k)).map((c) =>
    c.k === 'status'
      ? { ...c, tip: STATUS_TIP }
      : c.k === 'item_name'
      ? { ...c, tip: PRODUCT_TIP }
      : c
  ),
  PO_COL
]

/* The Excel export stays the full flat register — that is what data work needs
 * — so it keeps the link. The link moved onto the Product cell for READING; it
 * did not stop being data. Dispatch is the one column the export really does
 * lose, and only because it is now redundant: the Status column carries the
 * line's own stage, which is what Dispatch used to say.
 */
const EXPORT_COLS = [
  ...SHEET_COLS.filter((c) => c.k !== 'fulfilment_status'),
  PO_COL
]

// At-Apartment-specific band on top of the shared set: the day picker above
// the grid already names the day, so the Date column can fold away here. The
// HQ daily sheet deliberately does NOT get this — it mixes many days and the
// per-line date is load-bearing there.
const DATE_BAND = { id: 'date', label: 'Date', short: 'DATE', cols: ['sheet_date'] }
// A band MUST stay a contiguous run of the columns actually rendered (see the
// colSpan warning on COL_BANDS), so a dropped column comes out of its band too
// — Reference is left holding Reference ID alone.
const GRID_BANDS = [
  DATE_BAND,
  ...COL_BANDS.map((b) => ({
    ...b,
    cols: b.cols.filter((k) => !DROPPED_COLS.includes(k))
  }))
]

// Per-browser preference for which column bands are folded. Never shared, never
// sent anywhere — it only decides how this grid draws.
const BANDS_KEY = 'aptRequests.foldedBands'
const GROUP_KEY = 'aptRequests.groupBySegment'
import { apartmentPoSpecs } from 'app/pages/Procurement/components/apartmentPo'
import {
  downloadCsv,
  downloadOrderPdf,
  downloadOrderPdfSections
} from 'app/pages/Procurement/components/exportUtils'
import RequestDetail from './RequestDetail'
import VendorManager from './VendorManager'

// The daily-sheet experience, ported (2026-07-17 ask): ONE flat grid of line
// items with every column, editable IN the view. Draft rows edit inline cell
// by cell (save on blur, same as the Procurement Dashboard daily sheet); the
// top entry row adds a new line straight into the caller's open draft for
// that apartment. Submitted+ rows are read-only here — approvals happen in
// the Approvals tab / detail dialog, fulfilment in the HQ queue.
const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  grow: { flex: 1 },
  // No overflow box so the header sticks to the page (like the daily sheet).
  scroller: {},
  table: {
    minWidth: 1900,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100]
    }
  },
  cell: { padding: '2px 6px', fontSize: 13, whiteSpace: 'nowrap' },
  // ---- collapsible column bands (2026-08-26) ----
  // The band header row sits ABOVE the filter row, so it needs the lower `top`
  // and the filter row is pushed down by the band row's height.
  bandRow: {
    '& th': {
      top: 0,
      zIndex: 6,
      padding: '2px 6px',
      fontSize: 10,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      textAlign: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      lineHeight: 1.6
    }
  },
  bandRowSpacer: { cursor: 'default !important', background: 'transparent' },
  // The band header is the control that folds a run of columns away, so it has
  // to look like one. It was inheriting the plain grey header fill and read as
  // dead space (2026-08-26). OH orange, which nothing else in this grid uses.
  bandCell: {
    background: '#fdefea !important',
    color: '#f25e35',
    fontWeight: 700,
    borderBottom: '2px solid #f25e35 !important',
    '&:hover': { background: '#f9ddd2 !important' }
  },
  // Every body row carries a real background so the pinned cells can inherit
  // it. Without this the stripe is painted by the row and the frozen cells
  // inherit `transparent`, letting the scrolled columns show through them.
  tableRow: {
    background: '#fff',
    '&:nth-of-type(even)': { background: '#faf8f5' }
  },
  // A folded band collapses to one narrow clickable stub that stays in place,
  // so the columns are visibly folded rather than silently gone.
  // The folded-band marker. ⛔ The tint lives on the HEADER only: painting it
  // down the body as well drew a full-height pink column through the table,
  // which reads as a broken empty column rather than a folded band (2026-08-26).
  // ⛔ A folded band must read as a SEAM, not a column. At 26px it was an empty
  // channel cutting through every row — and two adjacent folded bands made it
  // twice as wide (2026-08-26). 10px is enough to see and click, narrow enough
  // that the rows still read as continuous. The band name lives in the tooltip
  // and in the header row above, not in the cell.
  stubCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: 9,
    lineHeight: 1,
    color: '#f25e35',
    background: '#fdefea'
  },
  // Body half: the row's own fill, so a fold is a gridline rather than a gap.
  stubBodyCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    background: 'inherit',
    borderRight: '1px solid #f0ddd4'
  },
  // The 10px stub carries a chevron, not the band name — the name is in the
  // tooltip and in the orange header row, which is where the label belongs.
  stubText: { fontSize: 11, fontWeight: 700, lineHeight: 1 },
  bandChip: { textTransform: 'none', fontSize: 12, padding: '2px 8px', minWidth: 0 },
  // Segment header row, matching the afterschool games board's group tone.
  groupRow: {
    cursor: 'pointer',
    '& td': {
      background: '#f7f4ee',
      fontWeight: 600,
      borderTop: '1px solid #e8e4dc'
    },
    '&:hover td': { background: '#f1ece2' }
  },
  groupMeta: { color: '#8a857c', fontWeight: 400, fontSize: 11, marginLeft: 7 },
  // Pinned columns. The page is the horizontal scroller (there is no overflow
  // box — see `scroller`), so sticky resolves against the viewport.
  // ⛔ The page is the horizontal scroller (there is no overflow box — see
  // `scroller`), so `left` on a sticky cell is measured from the VIEWPORT, and
  // the permanent nav drawer covers the first `drawerWidth` px of it. Freezing
  // at the bare column offset parks the pinned columns UNDER the sidebar, which
  // is what shipped on 2026-08-26 and was only visible in a real browser —
  // jsdom has no layout, so no test could have caught it.
  // The offset therefore rides in a custom property and the drawer width is
  // added in CSS, because below `sm` the drawer is a bottom sheet and takes no
  // horizontal room at all.
  pinned: {
    position: 'sticky',
    zIndex: 4,
    // ⛔ NOT a hard white. A pinned cell has to keep whatever the ROW is
    // painting — the zebra stripe, the hover, the group tint — or the frozen
    // columns read as a white block sitting on top of striped rows, which is
    // exactly what shipped on 2026-08-26. `inherit` takes the tr's background,
    // and `tableRow` below guarantees the tr always has a concrete one so an
    // inherited transparent never lets the scrolled content show through.
    background: 'inherit',
    left: 'var(--pin-x)',
    [theme.breakpoints.up('sm')]: {
      left: `calc(var(--pin-x) + ${drawerWidth}px)`
    }
  },
  pinnedHead: { zIndex: 7 },
  pinnedEdge: { boxShadow: '2px 0 0 rgba(0,0,0,.12)' },
  // Every cell is nowrap so the grid reads as one line per row, but two columns
  // hold free prose — a print spec runs to "colour - front & back - curved
  // edges. step 1: … + step 2: 90gsm basic lamination" — and one long value
  // stretched the column to its full single-line width, pushing everything
  // after it off the screen. These wrap inside their declared width instead.
  wrapCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top'
  },
  // See sheetColumns `wrap`: the width has to be pinned on all three of width /
  // min / max. max-width alone let the table collapse the column to min-content.
  filterHeadCell: { padding: '4px 6px' },
  filterInput: {
    backgroundColor: '#fff',
    '& input': { fontSize: 12, padding: '5px 6px' },
    '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 }
  },
  editInput: { '& input': { fontSize: 13, padding: '4px 2px' } },
  // The new-line row is a form, not a record. It used to carry a cream fill,
  // which read as a saved row sitting above the real ones. It now sits on the
  // plain ground and is told apart by a "new" label and the rule below it.
  entryRow: { background: 'transparent' },
  entryRowLast: {
    '& > td': { borderBottom: `2px solid ${theme.palette.divider}` }
  },
  entryLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main
  },
  dateText: {
    cursor: 'pointer',
    borderBottom: `1px dashed ${theme.palette.text.disabled}`,
    '&:hover': { borderBottomColor: theme.palette.primary.main }
  },
  draftChipBar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  bulkNote: { marginTop: theme.spacing(1) }
}))

// Center defaults to 'Corporate' for apartment lines (P3, 2026-07-18); new
// lines default to Goods with GST 18% (2026-07-18).
// Today in the browser's own timezone — toISOString() alone is UTC and would
// hand back yesterday before 05:30 IST.
const todayStr = () => {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10)
}

// One reading for the Date column, on every row — "5 Aug 2026", the format the
// petty-cash ledger already uses.
const fmtDate = (iso) => {
  const s = String(iso || '').slice(0, 10)
  if (!s) return ''
  const d = Dayjs(s)
  return d.isValid() ? d.format('D MMM YYYY') : s
}

/**
 * The Date cell: text until you click it, the browser's date picker while you
 * are changing it.
 *
 * Every other cell here is a live control, and this one is the exception on
 * purpose. A native date input draws its own format — 07/08/2026 on this
 * machine, 8/7/2026 on a US one — and no styling changes that, while a locked
 * row cannot host an input at all and printed the raw 2026-08-05. Text is the
 * only way the column reads the same on all three kinds of row.
 *
 * `onChange` fires on every keystroke (the entry row keeps its draft in sync so
 * Enter commits the date you just typed); `onCommit` fires on blur (saved rows
 * write through to the API there, as they did before).
 */
const DateCell = ({ value, editable, width, onChange, onCommit }) => {
  const classes = useStyles()
  const [editing, setEditing] = React.useState(false)
  const [local, setLocal] = React.useState('')

  if (!editable) return fmtDate(value) || '—'

  if (editing) {
    return (
      <TextField
        className={classes.editInput}
        type="date"
        autoFocus
        style={{ width: width || 90 }}
        value={local}
        onChange={(e) => {
          const v = e.target.value
          setLocal(v)
          if (onChange) onChange(v)
        }}
        onBlur={() => {
          setEditing(false)
          if (onCommit) onCommit(local)
        }}
      />
    )
  }

  const open = () => {
    setLocal(String(value || '').slice(0, 10))
    setEditing(true)
  }

  return (
    <span
      role="button"
      tabIndex={0}
      title="Click to change the date"
      className={classes.dateText}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}>
      {fmtDate(value) || 'set date'}
    </span>
  )
}

const emptyEntry = (date) => ({
  sheet_date: date || todayStr(),
  item_name: '',
  item_type: 'goods',
  category: '',
  vendor: '',
  centre_label: 'Corporate',
  qty: 1,
  print_specification: '',
  paper_kind: '',
  sides_per_file: '',
  ppp: '',
  gst_rate: 18,
  amount_inclusive: false,
  invoice_amount: '',
  file_link: '',
  reference_link: '',
  reference_id: '',
  source_of_request: ''
})

// Print detail carried over when a catalogue item is picked (2026-08-03). The
// catalogue only started holding these in migration 0053 — before that the
// requester retyped the spec on every line, and an unset item_type meant a
// print item priced as goods. Only non-blank catalogue values win, so a
// half-filled catalogue row never wipes something already typed.
const printFieldsFrom = (it) => {
  const out = {}
  if (it.item_type) out.item_type = it.item_type
  if (it.print_specification) out.print_specification = it.print_specification
  if (it.paper_kind) out.paper_kind = it.paper_kind
  if (it.sides_per_file != null && it.sides_per_file !== '') {
    out.sides_per_file = it.sides_per_file
  }
  if (it.capex_opex) out.capex_opex = it.capex_opex
  return out
}

// The catalogue-only grouping for the sheet's general/common supplies. Sent in
// the `category` slot of the add-category call, which the backend recognises as
// a grouping rather than a budget head (2026-08-03).
const GENERAL_GROUP = 'general'

// The cells an LXD must fill before a line can be added (P5, 2026-07-18).
const REQUIRED_FIELDS = [
  ['item_name', 'Product'],
  ['category', 'Category'],
  ['qty', 'Units'],
  ['vendor', 'Vendor']
]

const missingRequired = (row) =>
  REQUIRED_FIELDS.filter(([k]) => {
    const v = row[k]
    if (k === 'qty') return !(Number(v) > 0)
    return !String(v || '').trim()
  }).map(([, label]) => label)

// Columns a bulk-upload template carries (the user-fillable ones). Header text
// is what appears in the CSV; key is the API field.
const BULK_COLS = [
  ['Center', 'centre_label'],
  ['Type (goods/print)', 'item_type'],
  ['Vendor', 'vendor'],
  ['Category', 'category'],
  ['Reference ID', 'reference_id'],
  ['Product', 'item_name'],
  ['File link', 'file_link'],
  ['Print specification', 'print_specification'],
  ['Kind of Paper', 'paper_kind'],
  ['Sides per file', 'sides_per_file'],
  ['Units', 'qty'],
  ['Rate', 'ppp'],
  ['GST %', 'gst_rate'],
  ['GST inclusive (yes/no)', 'amount_inclusive'],
  ['Invoice amount (if inclusive)', 'invoice_amount'],
  ['Link', 'reference_link'],
  ['Source', 'source_of_request']
]

// Sample row for the template, keyed by field — mapped through BULK_COLS so the
// two never drift.
const BULK_SAMPLE = {
  centre_label: 'Corporate',
  item_type: 'goods',
  category: '',
  item_name: 'Example item — delete this row',
  qty: '10',
  ppp: '50',
  gst_rate: '18',
  amount_inclusive: 'no',
  source_of_request: 'WhatsApp'
}

// The Status chip names the stage THIS LINE has reached — see `lineStage` in
// requestStage.js for why it is the line and not the request rolled up.
// `request` supplies the approval chain and the receiver's name; `status` is
// the fallback for a caller that only has the status string.
const StatusChip = ({ request, line, status }) => {
  const meta =
    request || line
      ? lineStage(request, line)
      : STATUS_META[status] || { label: status, color: '#999' }
  return (
    <Chip
      size="small"
      label={meta.label}
      style={{
        background: meta.color,
        color: '#fff',
        height: 20,
        fontSize: 11
      }}
    />
  )
}

/* ⭐ The line's link, as an arrow on the Product cell (Satya, 2026-09-02: "we
 * need to remove the column of the link, we will have the link in the product
 * itself, there will be a small arrow button, which will act as the link
 * button"). The File/Link column is gone from this grid.
 *
 * On a DRAFT line the arrow also SETS it — with the column gone there is
 * nowhere else to type one, and the catalogue only auto-fills the links it
 * happens to know. Muted = no link yet, solid = there is one; on a locked line
 * with no link there is no button at all, so the column stays quiet.
 * ⛔ The PO export keeps its own File column, deliberately untouched.
 */
const LineLink = ({ row, editable, onSave }) => {
  const [anchor, setAnchor] = React.useState(null)
  const link = row.reference_link || ''
  const open = () => window.open(link, '_blank', 'noopener,noreferrer')
  if (!link && !editable) return null
  return (
    <>
      <IconButton
        size="small"
        // Orange, because that is what a link looks like everywhere else in
        // Alfred (Satya, 2026-09-02) — and it is what the File/Link column this
        // replaces already did: a MUI <Link> takes palette.primary, which is
        // oh-orange. Taken from the theme, never hardcoded.
        color="primary"
        disableRipple
        style={{
          padding: 0,
          flex: '0 0 auto',
          opacity: link ? 1 : 0.45,
          backgroundColor: 'transparent'
        }}
        title={
          editable
            ? link || 'Add the artwork / purchase link for this line'
            : link
        }
        onClick={(e) => (editable ? setAnchor(e.currentTarget) : open())}
      >
        <CallMadeIcon style={{ fontSize: 14 }} />
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={1} display="flex" alignItems="center">
          <TextField
            autoFocus
            placeholder="Paste the artwork / purchase link"
            defaultValue={link}
            style={{ width: 320 }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              onSave(row, e.target.value.trim())
              setAnchor(null)
            }}
            onBlur={(e) => onSave(row, e.target.value.trim())}
          />
          {link && (
            <Button size="small" onClick={open}>
              Open
            </Button>
          )}
        </Box>
      </Popover>
    </>
  )
}

/* ⭐⭐ The merged Status cell (Satya, 2026-09-02: "can we have the status and
 * dispatch same? they both should represent the same thing"). The chip says
 * where the line IS; the arrow beside it MOVES the line on. One column, one
 * vocabulary, one truth — the Dispatch column it replaces is gone from this
 * grid (the HQ daily sheet keeps its own).
 *
 * Click the CHIP for the item's full dated life (the tracking popup); click the
 * ARROW to set the stage. The arrow only appears on a line that can actually be
 * moved right now: before final approval nothing has been ordered yet, and
 * editing a submitted row would knock the whole request back to draft. Same
 * gate the Dispatch column used, so nothing became settable that was not.
 */
const SETTABLE_STATUSES = ['final_approved', 'in_daily', 'handed_over']

const StageCell = ({ row, onTrack, onPick, disabled }) => {
  const [anchor, setAnchor] = React.useState(null)
  const settable = !row.removed && SETTABLE_STATUSES.includes(row.status)
  const current = row.fulfilment_status || 'pending'
  const choose = (value) => {
    setAnchor(null)
    onPick(row, value)
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{ cursor: 'pointer' }}
        title="Click for this item's full dated tracking"
        onClick={() => onTrack(row.request)}
      >
        <StatusChip request={row.request} line={row} status={row.status} />
      </span>
      {settable && (
        <>
          <IconButton
            size="small"
            disabled={disabled}
            title="Move this line to another stage"
            style={{ padding: 0 }}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchor}
            open={!!anchor}
            onClose={() => setAnchor(null)}
          >
            {DISPATCH_OPTIONS.map(([value, label]) => (
              <MenuItem
                key={value}
                selected={value === current}
                onClick={() => choose(value)}
              >
                {label}
              </MenuItem>
            ))}
            <MenuItem disabled divider style={{ fontSize: 11, opacity: 0.7 }}>
              after HQ has the goods
            </MenuItem>
            {downstreamOptionsFor(row.request).map((o) => (
              <MenuItem
                key={o.value}
                disabled={o.disabled}
                title={o.reason || undefined}
                onClick={() => choose(o.value)}
              >
                {o.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </span>
  )
}

// Product search for the entry row: type a name or reference id and pick an At
// Apartment catalogue item to auto-fill the line (parity with the daily sheet).
function AptCatalogueField({ value, onType, onPick, width }) {
  const [open, setOpen] = React.useState(false)
  const [results, setResults] = React.useState([])
  const cache = React.useRef(null)
  const anchorRef = React.useRef(null)

  const run = async (text) => {
    const q = String(text || '').trim().toLowerCase()
    try {
      if (!cache.current) {
        const res = await fetchAptCatalogue({ status: 'approved' })
        cache.current = Array.isArray(res) ? res : res?.results || []
      }
      setResults(
        cache.current
          .filter((it) => {
            const n = String(it.item_name || it.name || '').toLowerCase()
            const r = String(it.reference_id || '').toLowerCase()
            // Segment too (2026-08-07): the segment is the game a card belongs
            // to — "Shape Fusion", "Guess Me". Matching only name and reference
            // id meant typing the game found nothing while typing one of its
            // cards found that card, which read as the search being broken.
            const s = String(it.segment || '').toLowerCase()
            return !q || n.includes(q) || r.includes(q) || s.includes(q)
          })
          .slice(0, 15)
      )
    } catch (e) {
      setResults([])
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <TextField
        inputRef={anchorRef}
        size="small"
        placeholder="Search product / ref id…"
        style={{ width: width || 170 }}
        InputProps={{ style: { fontSize: 13 } }}
        value={value || ''}
        onChange={(e) => {
          onType(e.target.value)
          setOpen(true)
          run(e.target.value)
        }}
        onFocus={() => {
          setOpen(true)
          run(value)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      <Popper open={open && results.length > 0} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1400 }}>
        <Paper elevation={4} style={{ maxHeight: 280, overflowY: 'auto', minWidth: 260 }}>
          {results.map((it) => (
            <MenuItem
              key={it.reference_id || it.uuid || it.item_name}
              onMouseDown={() => {
                onPick(it)
                setOpen(false)
              }}>
              <span style={{ fontSize: 13 }}>
                {it.item_name || it.name}
                {it.reference_id ? <span style={{ color: '#999' }}> · {it.reference_id}</span> : null}
                {it.category ? <span style={{ color: '#bbb' }}> · {it.category}</span> : null}
              </span>
            </MenuItem>
          ))}
        </Paper>
      </Popper>
    </div>
  )
}

// The chase-list row a dashboard click arrived with, expressed as this grid's
// own column filters: item into Product, apartment into Center/Apartment. The
// grid then reads as though the person typed it into the header themselves,
// which also means they can clear it there.
const focusFilters = (focus) => {
  if (!focus) return {}
  const next = {}
  if (focus.item) next.item_name = focus.item
  if (focus.apartment) next.centre_label = focus.apartment
  return next
}

const RequestsTab = ({ focus, onFocusUsed }) => {
  const classes = useStyles()
  const role = React.useContext(RoleContext)
  const headerSlot = React.useContext(HeaderSlotContext)
  // Categories added from Alfred (2026-09-17): re-render the grid's Category
  // dropdowns when the list loads or grows. Above the early return (#310).
  const categories = useAptCategories()
  const [askNewCategory, newCategoryDialog] = useNewCategoryPrompt()
  const [apartments, setApartments] = React.useState([])
  const [vendors, setVendors] = React.useState([])
  const [requests, setRequests] = React.useState(null)
  const [filters, setFilters] = React.useState(() => focusFilters(focus))
  // A LIST of blank draft rows, spreadsheet-style: "+ add line" appends another
  // (user 2026-07-23). A draft persists once it has its required fields and you
  // leave it or press Enter, then shows as a real per-cell-saving row.
  const [entryRows, setEntryRows] = React.useState(() => [emptyEntry()])
  // Declared here, ABOVE the `if (!requests) return` — a hook after a
  // conditional return changes the hook count and crashes the grid (React
  // #310, 2026-07-23).
  const entryRowRef = React.useRef(null)
  // Entry row is always on the grid (2026-07-23) — keep adding lines; remove a
  // saved one with its delete button. No open/close toggle.
  const entryOpen = true
  const [entryApt, setEntryApt] = React.useState('')
  const [detail, setDetail] = React.useState(null)
  const [vendorMgr, setVendorMgr] = React.useState(false)
  const [bulk, setBulk] = React.useState(null)
  const [bulkCat, setBulkCat] = React.useState(null) // {apartment, category, error?}
  const [busy, setBusy] = React.useState(false)
  // What the API refused, shown above the grid. Without this a rejected save
  // looked like a dead button (2026-07-22).
  const [error, setError] = React.useState('')
  const [selected, setSelected] = React.useState(() => new Set())
  const [confirmDel, setConfirmDel] = React.useState(false)
  const [draftsOpen, setDraftsOpen] = React.useState(false) // open-drafts list
  const [confirmCancel, setConfirmCancel] = React.useState(false) // cancel removed lines
  const [confirmReopenBulk, setConfirmReopenBulk] = React.useState(false) // bulk reopen
  // Hand-over is REQUEST-level: picking it on one line moves the whole PO, so
  // it asks first. The 2026-08-31 accident (46 lines handed over by a stray
  // keypress on a Chip's delete slot) is exactly this action with no confirm.
  const [confirmHandOver, setConfirmHandOver] = React.useState(null)
  const [trackReq, setTrackReq] = React.useState(null) // status → vertical timeline
  const [approvalReq, setApprovalReq] = React.useState(null) // {req, anchorEl}
  const [approvalForm, setApprovalForm] = React.useState({})
  const [reopenReq, setReopenReq] = React.useState(null) // approved -> draft
  const [lockedInfo, setLockedInfo] = React.useState(false) // "sent to HQ" explainer
  const [removedInfo, setRemovedInfo] = React.useState(false) // "removed by HQ" explainer
  const [dragUuid, setDragUuid] = React.useState(null) // line being dragged
  // Day navigation + period filter, ported from the HQ daily sheet (2026-07-25):
  // browse enquiries by day, or filter by FY / month / custom range.
  // Opens on today rather than on every day at once. "Show all days" clears it
  // back to '', which is still what the rest of the filtering reads as no-day.
  const [day, setDay] = React.useState(todayStr())

  // A second click while already on this tab has to re-seed: the tab does not
  // remount, so the initial filter state above never runs again. `day` clears
  // too — a chased line is usually not on the day being viewed, and landing on
  // an empty grid reads as "the row is gone".
  const focusKey = focus ? `${focus.item || ''}|${focus.apartment || ''}` : ''
  React.useEffect(() => {
    if (!focusKey) return
    setFilters(focusFilters(focus))
    setDay('')
    if (onFocusUsed) onFocusUsed()
    // focusKey identifies a NEW focus; re-running on the object identity would
    // loop, since onFocusUsed clears it.
  }, [focusKey])
  const [fy, setFy] = React.useState('')
  const [month, setMonth] = React.useState('')
  const [dateFrom, setDateFrom] = React.useState('')
  const [dateTo, setDateTo] = React.useState('')
  const [periodAnchor, setPeriodAnchor] = React.useState(null)
  // Single "Actions ▾" menu collapsing the toolbar's add/import/export/manage
  // buttons (2026-07-25 ask) — the bar was 8 buttons wide.
  const [actionsAnchor, setActionsAnchor] = React.useState(null)
  // Column-aligned bulk edit (ported): fill any columns in the highlighted row,
  // Apply once → every ticked row gets them. Replaces the one-field dropdown.
  const [bulkValues, setBulkValues] = React.useState({})
  // Sign-off typed on a new-line entry row, applied to the request the line
  // lands in once it is saved (ported from the daily sheet).
  const EMPTY_APPROVAL = {
    approval_needed: '',
    approved_by_name: '',
    approval_source: '',
    approval_reference: ''
  }
  const [entryApproval, setEntryApproval] = React.useState(EMPTY_APPROVAL)
  const entryApprovalSet = Object.values(entryApproval).some(Boolean)

  const load = React.useCallback(() => {
    // Only what At Apartment raised. Lines HQ typed on the daily sheet — or
    // pulled in from the Google Sheet — never belong on this screen; the flow
    // is one-way, out to the daily sheet once the head approves.
    fetchAptRequests({ lines: '1', origin: 'apartment' }).then(setRequests)
  }, [])

  const loadVendors = React.useCallback(() => {
    fetchAptVendors({ status: 'approved' }).then(setVendors)
  }, [])

  React.useEffect(() => {
    // No default apartment (2026-09-17): the row had no picker, so every new
    // line silently went to the first apartment in the list (At Apartment HQ).
    // The apartment is now chosen in the row's Center/Apartment cell.
    fetchAptList().then(setApartments)
    loadVendors()
  }, [loadVendors])
  React.useEffect(load, [load])

  // New-line date follows the day you're viewing — pick an old date at the top
  // and new lines fill on that date. Only re-dates untouched (no-product) entry
  // rows; once a product is typed the date is left alone (still editable).
  React.useEffect(() => {
    const d = day || todayStr()
    setEntryRows((ds) => ds.map((e) => (e.item_name ? e : { ...e, sheet_date: d })))
  }, [day])

  // ---------------------------------------------------------- column bands
  // Shared with the HQ daily sheet: the two grids render the same column set,
  // so folding lives in one hook rather than being copied into both.
  const {
    bandOf,
    displayCols,
    pinFor,
    lastPinned,
    toggleBand
    // 4th arg (lead width) stays the shared default; the 5th adds this grid's
    // own Date band on top of the shared set.
  } = useColumnBands(GRID_COLS, BANDS_KEY, PINNED_COLS, undefined, GRID_BANDS)

  // Segment grouping — state only; the grouping itself happens below, where
  // `rows` exists (see useSegmentGroups on why this is split).
  const {
    grouped,
    toggleGrouped,
    collapsed: collapsedGroups,
    toggleGroup,
    expandAll,
    collapseAllOf
  } = useSegmentGroups(GROUP_KEY)

  // One place decides a grid cell's pinning, so the header and the body can
  // never drift apart and develop a seam.
  const pinProps = React.useCallback(
    (col, extraClass) => {
      const pin = pinFor(col)
      if (!pin) return { className: extraClass }
      return {
        className: [
          extraClass,
          classes.pinned,
          col.k === lastPinned ? classes.pinnedEdge : ''
        ]
          .filter(Boolean)
          .join(' '),
        style: {
          '--pin-x': `${pin.left}px`,
          width: pin.width,
          minWidth: pin.width,
          maxWidth: pin.width
        }
      }
    },
    [pinFor, lastPinned, classes]
  )

  // The band header row, built by walking the same display sequence: a run of
  // banded columns becomes one colSpan header, a folded band becomes its stub,
  // and everything ungrouped becomes a blank spacer.
  const bandHeaderCells = React.useMemo(
    () => bandHeaderRow(displayCols, classes, toggleBand, bandOf),
    [displayCols, classes, toggleBand, bandOf]
  )

  // Map the display sequence to cells. `fn(col)` renders a real column; a
  // folded band yields its clickable stub.
  const mapGridCells = React.useCallback(
    (fn) => mapBandedCells(displayCols, classes, toggleBand, fn),
    [displayCols, classes, toggleBand]
  )

  if (!requests) return <LinearProgress />

  // Flatten requests -> one sheet row per line (adds Sl No. per request).
  // Editable until somebody approves it (2026-07-22 rule): a list that is still
  // with the cluster manager can be fixed — saving pulls it back to draft, so
  // the approver never signs off a stale version. From 'With At Apartment head'
  // on it is frozen, and only the head or HQ can reopen it.
  const EDITABLE_STATUS = ['draft', 'rejected', 'submitted']
  const mayEdit = role.can_request || role.is_central || role.is_hq
  const allRows = toSheetRows(requests).map((row) => ({
    ...row,
    editable: EDITABLE_STATUS.includes(row.status) && mayEdit,
    // Approved but not yet on HQ's daily sheet — the head / HQ can unlock it.
    reopenable:
      (row.status === 'cluster_approved' || row.status === 'final_approved') &&
      (role.is_central || role.is_hq),
    // Already on HQ's daily sheet / handed over — HQ is procuring it, so it can't
    // be ticked or deleted from here. Shown with a lock + explainer (not a blank
    // row that reads as a bug).
    sentToHq: ['in_daily', 'handed_over', 'closed'].includes(row.status),
    // Being procured and not yet marked delivered — can be bulk-marked
    // "received by HQ procurement team" straight from the enquiry grid.
    receivable:
      ['in_daily', 'handed_over'].includes(row.status) &&
      row.fulfilment_status !== 'delivered' &&
      !row.removed_by_hq,
    // HQ removed this line — tombstoned so the At Apartment team can see it and re-send.
    removed: !!row.removed_by_hq
  }))

  const matches = matchesFilter

  // A line's own day, falling back to when its request was raised for the ones
  // written before lines carried a date.
  const dateOf = (row) =>
    String(row.sheet_date || (row.request && row.request.created) || '').slice(0, 10)

  // Financial year / month a row belongs to (by request.created), for the
  // Period filter — ported from the daily sheet.
  const fyOf = (row) => {
    const d = String((row.request && row.request.created) || '').slice(0, 10)
    if (!d) return ''
    const y = Number(d.slice(0, 4))
    const m = Number(d.slice(5, 7))
    const start = m >= 4 ? y : y - 1
    return `FY ${start}-${String((start + 1) % 100).padStart(2, '0')}`
  }
  const monthOf = (row) => String((row.request && row.request.created) || '').slice(0, 7)
  const monthLabel = (key) => {
    const [y, m] = key.split('-')
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${names[Number(m) - 1]} ${y}`
  }
  const shiftDay = (delta) => {
    const d = new Date(`${day || todayStr()}T00:00:00`)
    d.setDate(d.getDate() + delta)
    setDay(
      new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
    )
  }

  // Plain text of a cell, for the Excel/PDF export (parity with the daily
  // sheet, 2026-07-23).
  const cellText = (row, col) => {
    if (col.k === 'status') {
      return lineStageText(row.request, row) || row.status || ''
    }
    if (col.kind === 'date') return dateOf(row)
    if (col.kind === 'at_apartment') return 'Yes'
    if (col.kind === 'centre') {
      return row.apartment_name || row.centre_label || ''
    }
    if (col.kind === 'approval') {
      const r = row.request || {}
      return [r.approved_by_name, r.approval_source, r.approval_reference]
        .filter(Boolean)
        .join(' · ')
    }
    const v = row[col.k]
    return v == null ? '' : String(v)
  }

  const exportData = () => ({
    filename: 'at-apartment-requests',
    title: 'At Apartment — Requests',
    headers: EXPORT_COLS.map((c) => c.label),
    rows: rows.map((row) => EXPORT_COLS.map((c) => cellText(row, c)))
  })

  // What cut the view down, for the PO's "part of the full list" line.
  const filterNote = () => {
    const parts = []
    if (day) parts.push(`Day: ${day}`)
    if (month) parts.push(monthLabel(month))
    if (fy) parts.push(fy)
    if (dateFrom || dateTo) parts.push(`${dateFrom || '…'} – ${dateTo || '…'}`)
    GRID_COLS.forEach((c) => {
      if (c.filter && filters[c.k]) parts.push(`${c.label}: ${filters[c.k]}`)
    })
    return parts.join(' · ')
  }

  // Excel / PDF export invoked from the Actions menu (ExportButtons inlined).
  // The PDF is a PO document, not the register (apartmentPo.js, shared with
  // the HQ daily sheet): one PO per request in view, ticked rows when any are
  // ticked, else everything the filters left visible. Excel stays the full
  // flat sheet — that is what data work needs.
  const runExport = (kind) => {
    setActionsAnchor(null)
    if (kind === 'excel') {
      const d = exportData()
      if (!d || !d.rows || !d.rows.length) return
      downloadCsv(d.filename || 'export', d.headers, d.rows)
      return
    }
    const source = selected.size
      ? rows.filter((r) => selected.has(r.uuid))
      : rows
    const specs = apartmentPoSpecs({
      rows: source,
      filterNote: selected.size
        ? `${selected.size} ticked line${selected.size === 1 ? '' : 's'}`
        : filterNote()
    })
    if (!specs.length) return
    if (specs.length === 1) downloadOrderPdf(specs[0].filename, specs[0])
    else downloadOrderPdfSections('at-apartment-purchase-orders', specs)
  }

  const rows = allRows.filter(
    (row) =>
      (!fy || fyOf(row) === fy) &&
      (!month || monthOf(row) === month) &&
      (!day || dateOf(row) === day) &&
      (!dateFrom || dateOf(row) >= dateFrom) &&
      (!dateTo || dateOf(row) <= dateTo) &&
      GRID_COLS.every((c) => {
        if (!c.filter || !filters[c.k]) return true
        // Match what the CELL SHOWS, not the raw field. Center/Apartment
        // renders `apartment_name || centre_label` (an At Apartment line carries no
        // centre_label at all), so filtering on row.centre_label could never
        // match an apartment by name — typing "Prestige Kews" into that header
        // returned nothing, and a dashboard row that seeds it landed on an
        // empty grid. Status has always resolved its chip label for the same
        // reason.
        const value =
          c.k === 'status'
            ? lineStageText(row.request, row) || row.status
            : c.kind === 'centre'
            ? row.apartment_name || row.centre_label || ''
            : // ⭐⭐ Product searches the GAME as well as the item (Satya,
            // 2026-09-02: "we cannot search 'tale trail' from the searchable
            // box in the top, hence it becomes difficult"). A game's lines are
            // named after the PIECES — Tale trail is Story Mat / Object cards /
            // Character cards — so the game's own name appears in no cell, and
            // typing it returned an empty grid. The catalogue picker learned
            // exactly this on 2026-08-07; the header filter never did.
            // ⛔ Joined with ' · ', not a space: a space would let a query that
            // straddles the two fields ("cards tale") match by accident.
            c.k === 'item_name'
            ? [row.item_name, row.segment].filter(Boolean).join(' · ')
            : row[c.k]
        return matches(value, filters[c.k])
      })
  )

  const months = [...new Set(allRows.map(monthOf).filter(Boolean))].sort().reverse()
  const fyList = [...new Set(allRows.map(fyOf).filter(Boolean))].sort().reverse()
  const periodLabel = (() => {
    if (dateFrom || dateTo) return `${dateFrom || '…'} – ${dateTo || '…'}`
    if (month) return monthLabel(month)
    if (fy) return fy
    return 'All time'
  })()
  const periodActive = !!(dateFrom || dateTo || month || fy)
  const clearPeriod = () => {
    setFy('')
    setMonth('')
    setDateFrom('')
    setDateTo('')
  }
  // Register totals for the currently-shown rows (ported from the daily sheet).
  // Removed-by-HQ lines are cancelled, so they don't count towards totals.
  const totals = rows.reduce(
    (a, r) =>
      r.removed
        ? a
        : {
            taxable: a.taxable + Number(r.line_total || 0),
            gst: a.gst + Number(r.gst_amount || 0),
            invoice: a.invoice + Number(r.invoice_amount || 0)
          },
    { taxable: 0, gst: 0, invoice: 0 }
  )

  const drafts = requests.filter(
    (r) =>
      (r.status === 'draft' || r.status === 'rejected') && (r.lines || []).length
  )

  const vendorNames = vendors.map((v) => v.name)

  // Row selection. Editable rows bulk-edit; removed (tombstoned) rows bulk
  // re-send or cancel. Both are selectable; select-all covers either kind.
  const editableRows = rows.filter((r) => r.editable)
  const selectableRows = rows.filter(
    (r) => r.editable || r.removed || r.reopenable || r.receivable
  )
  const selectedEditable = rows.filter((r) => selected.has(r.uuid) && r.editable)
  const selectedRemoved = rows.filter((r) => selected.has(r.uuid) && r.removed)
  const selectedReopenable = rows.filter(
    (r) => selected.has(r.uuid) && r.reopenable && !r.editable && !r.removed
  )
  const selectedReceivable = rows.filter(
    (r) => selected.has(r.uuid) && r.receivable && !r.editable
  )
  const reopenReqCount = new Set(selectedReopenable.map((r) => r.request.uuid)).size
  const allSelectableSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.uuid))
  const someSelected = selected.size > 0 && !allSelectableSelected
  const toggleSel = (uuid) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  const toggleAll = () =>
    setSelected(
      allSelectableSelected ? new Set() : new Set(selectableRows.map((r) => r.uuid))
    )
  const setBulkVal = (k, v) => setBulkValues((p) => ({ ...p, [k]: v }))
  const bulkHasValues = Object.values(bulkValues).some((v) => v !== '' && v != null)

  // Apply the filled bulk-row columns to every ticked (editable) row at once.
  // Setting the dispatch state stamps its date automatically (ported).
  const applyBulkRow = async () => {
    const filled = Object.entries(bulkValues).filter(([, v]) => v !== '' && v != null)
    const targets = rows.filter((r) => selected.has(r.uuid) && r.editable)
    if (!filled.length || !targets.length) return
    const patch = {}
    // eslint-disable-next-line no-restricted-syntax
    for (const [k, v] of filled) patch[k] = NUM_FIELDS.has(k) ? Number(v) : v
    if (patch.fulfilment_status) {
      const today = todayStr()
      if (patch.fulfilment_status === 'ordered') patch.ordered_on = today
      if (patch.fulfilment_status === 'dispatched') patch.dispatched_on = today
      if (patch.fulfilment_status === 'delivered') patch.delivered_on = today
    }
    setBusy(true)
    setError('')
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of targets) {
        // eslint-disable-next-line no-await-in-loop
        await updateAptLine({ uuid: r.uuid, ...patch })
      }
      setSelected(new Set())
      setBulkValues({})
      load()
    } catch (e) {
      setError(`Could not apply to the selected lines — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // Fill the bulk Product from the At Apartment catalogue — drops the name plus the
  // details it carries into the bulk row so Apply pushes them to all the ticked
  // rows at once (ported).
  const pickBulkCatalogue = (it) => {
    setBulkValues((p) => ({
      ...p,
      item_name: it.item_name || it.name || p.item_name,
      ppp: it.ppp != null && it.ppp !== '' ? it.ppp : p.ppp,
      reference_link: it.reference_link || p.reference_link,
      reference_id: it.reference_id || p.reference_id,
      // The grid's own heads ("5+ Robotics"), the same list as its Category
      // column — not the HQ daily sheet's At Apartment list ("5-12 Robotics").
      category: matchAptCategory(it.category) || p.category,
      vendor: it.vendor || p.vendor,
      // ⭐⭐ segment AND the second artwork file ride along too (2026-09-02).
      // Every pick path copied vendor but not these: a hand-added line landed
      // blank-segment (so it grouped nowhere, quietly undoing the 276-row
      // backfill) and with only the front PDF. The backend's bulk payload has
      // carried segment since #737 — only these four FE paths were left behind.
      segment: it.segment || p.segment,
      file_link: it.file_link || p.file_link,
      ...printFieldsFrom(it)
    }))
  }

  // Remove one unsaved draft entry row via its own delete button; always keep at
  // least one blank row to type into (ported).
  const removeDraft = (draft) => {
    setEntryRows((ds) => {
      const next = ds.filter((d) => d !== draft)
      return next.length ? next : [emptyEntry(day || todayStr())]
    })
  }

  // Delete every ticked (editable) row — the type-to-confirm dialog gates it.
  const deleteSelected = async () => {
    const targets = rows.filter((r) => selected.has(r.uuid) && r.editable)
    if (!targets.length) return
    setConfirmDel(false)
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of targets) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAptLine(r.uuid)
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }

  // Bulk actions on the ticked REMOVED lines: re-send them all to procure, or
  // cancel them all for good.
  const resendSelectedRemoved = async () => {
    if (!selectedRemoved.length) return
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of selectedRemoved) {
        // eslint-disable-next-line no-await-in-loop
        await resendAptLine(r.uuid)
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }
  const cancelSelectedRemoved = async () => {
    if (!selectedRemoved.length) return
    setConfirmCancel(false)
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of selectedRemoved) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAptLine(r.uuid)
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }

  // Bulk "received by HQ procurement team" straight from the enquiry grid: mark
  // the ticked lines delivered (which stocks HQ-store items into inventory).
  const markReceivedByHq = async () => {
    if (!selectedReceivable.length) return
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of selectedReceivable) {
        // eslint-disable-next-line no-await-in-loop
        await updateAptLine({ uuid: r.uuid, fulfilment_status: 'delivered' })
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }

  // Item-level counterpart of the bulk bar: goods arrive one parcel at a time,
  // so a single line must be markable without ticking it first.
  const markLineReceived = async (row) => {
    setError('')
    setBusy(true)
    try {
      await updateAptLine({ uuid: row.uuid, fulfilment_status: 'delivered' })
      load()
    } catch (e) {
      setError(`Could not mark the line received — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // Bulk reopen: the ticked approved (locked) lines' requests go back to draft.
  const reopenSelected = async () => {
    const reqUuids = [...new Set(selectedReopenable.map((r) => r.request.uuid))]
    if (!reqUuids.length) return
    setConfirmReopenBulk(false)
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const uuid of reqUuids) {
        // eslint-disable-next-line no-await-in-loop
        await aptRequestAction(uuid, 'reopen')
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }

  // `req` null = the new-line entry row: there is no request to stamp yet, so
  // the sign-off is held in `entryApproval` and applied once the line is saved.
  const openApproval = (req, anchorEl) => {
    setApprovalReq({ req, anchorEl, entry: !req })
    setApprovalForm(
      req
        ? {
            approval_needed: req.approval_needed || '',
            approved_by_name: req.approved_by_name || '',
            approval_source: req.approval_source || '',
            approval_reference: req.approval_reference || ''
          }
        : { ...entryApproval }
    )
  }
  // Bulk approval: fill the sign-off once, stamp it on every request behind the
  // ticked lines (a request is stamped once even if several of its lines are
  // selected).
  const bulkReqIds = () => [
    ...new Set(rows.filter((r) => selected.has(r.uuid)).map((r) => r.request.uuid))
  ]
  const openBulkApproval = (anchorEl) => {
    setApprovalReq({ req: null, anchorEl, bulk: true, count: bulkReqIds().length })
    setApprovalForm({
      approval_needed: '',
      approved_by_name: '',
      approval_source: '',
      approval_reference: ''
    })
  }
  const submitApproval = async () => {
    if (!approvalReq) return
    if (approvalReq.entry) {
      // Nothing to save yet — hold it against the line being typed.
      setEntryApproval({ ...approvalForm })
      setApprovalReq(null)
      return
    }
    setBusy(true)
    try {
      if (approvalReq.bulk) {
        // eslint-disable-next-line no-restricted-syntax
        for (const uuid of bulkReqIds()) {
          // eslint-disable-next-line no-await-in-loop
          await saveAptApproval({ uuid, ...approvalForm })
        }
        setApprovalReq(null)
        setSelected(new Set())
        load()
        return
      }
      await saveAptApproval({ uuid: approvalReq.req.uuid, ...approvalForm })
      setApprovalReq(null)
      load()
    } finally {
      setBusy(false)
    }
  }

  // The three post-HQ hops are not fulfilment values, so they do not go through
  // saveCell. Each routes to the call that actually records it:
  //   handed to At Apartment  -> request action, moves the WHOLE order
  //   dispatched by At Apt    -> per-item lifecycle event for this line
  //   received at apartment   -> this line's receipt
  const applyStage = async (row, stage) => {
    setBusy(true)
    setError('')
    try {
      const reqUuid = row.request && row.request.uuid
      if (stage === 'handed_to_at_apartment') {
        await aptRequestAction(reqUuid, 'hand_over')
      } else if (stage === 'dispatched_by_at_apartment') {
        await aptRequestAction(reqUuid, 'dispatch_from_at_apartment', '', {
          lines: [row.uuid]
        })
      } else if (stage === 'received_at_apartment') {
        await updateAptLine({ uuid: row.uuid, receipt_status: 'received' })
      }
      load()
    } catch (e) {
      setError(apiErrorText(e))
    } finally {
      setBusy(false)
    }
  }

  // Picking from the Dispatch cell: a stage value routes above, anything else
  // is an ordinary fulfilment_status save.
  const pickDispatch = (row, value) => {
    if (!isStageValue(value)) return saveCell(row, 'fulfilment_status', value)
    const stage = stageOf(value)
    if (stage === 'handed_to_at_apartment') return setConfirmHandOver(row)
    return applyStage(row, stage)
  }

  const saveCell = async (line, field, raw) => {
    let value = raw
    if (NUM_FIELDS.has(field)) value = raw === '' ? '' : Number(raw)
    if (String(line[field] ?? '') === String(value ?? '')) return
    setError('')
    try {
      await updateAptLine({ uuid: line.uuid, [field]: value })
    } catch (e) {
      setError(`Could not save that cell — ${apiErrorText(e)}`)
    }
    load()
  }

  // "+ add line" now reuses the blank row that is already open and only opens a
  // second once the first has a product (user 2026-08-07). Clicking it twice
  // used to stack two empty rows above the data, which is what made the strip
  // read as a pile of half-finished records. A row counts as blank on the same
  // signal the day-effect uses: no product typed yet.
  const addLineFromTop = () => {
    const blankIdx = entryRows.findIndex((e) => !e.item_name)
    if (blankIdx === -1) {
      setEntryRows((ds) => [...ds, emptyEntry(day || todayStr())])
    }
    const targetIdx = blankIdx === -1 ? entryRows.length : blankIdx
    window.setTimeout(() => {
      const row =
        document.querySelector(`[data-entry-index="${targetIdx}"]`) ||
        entryRowRef.current
      if (!row) return
      // Scroll DOWN only — never sideways (user 2026-07-23). scrollIntoView and
      // a plain focus() both drag the wide grid horizontally; scroll the page by
      // Y alone and focus without a scroll.
      const y = row.getBoundingClientRect().top + window.scrollY - 160
      window.scrollTo({ top: y, behavior: 'smooth' })
      const input = row.querySelector('input:not([type="checkbox"])')
      if (input) input.focus({ preventScroll: true })
    }, 60)
  }

  // Enough typed on a draft to be worth saving (the apartment is chosen once,
  // up top, and applies to every draft).
  const draftReady = (d) => !!entryApt && missingRequired(d).length === 0
  // What still stops a draft from saving, in the words of the column headers.
  const draftMissing = (d) => [
    ...(entryApt ? [] : ['Apartment']),
    ...missingRequired(d)
  ]
  // Worth nagging about only once someone has started the row.
  const draftStarted = (d) =>
    !!(String(d.item_name || '').trim() || d.vendor || d.category)

  // Takes the draft OBJECT, not an index (the index shifts / the row can unmount
  // before the save fires, 2026-07-23).
  const commitDraft = async (draft, opts = {}) => {
    if (!draft) return
    if (!entryApt) {
      if (!opts.silent) setError('Pick the apartment for this line.')
      return
    }
    const miss = missingRequired(draft)
    if (miss.length) {
      if (!opts.silent) setError(`Fill ${miss.join(', ')} before saving the line.`)
      return
    }
    setBusy(true)
    setError('')
    try {
      // The add returns the whole request, so the sign-off typed on the entry
      // row can be stamped onto the request the line landed in.
      const created = await addAptLine({ apartment: entryApt, ...draft })
      if (entryApprovalSet && created && created.uuid) {
        try {
          await saveAptApproval({ uuid: created.uuid, ...entryApproval })
        } catch (e) {
          // Line saved; the approval stamp is best-effort.
        }
        setEntryApproval(EMPTY_APPROVAL)
      }
      setEntryRows((ds) => {
        const next = ds.filter((d) => d !== draft)
        return next.length ? next : [emptyEntry(day || todayStr())]
      })
      load()
    } catch (e) {
      setError(`Could not add the line — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // Copy a line into the same apartment's draft (P2, 2026-07-18).
  const copyLine = async (row) => {
    setBusy(true)
    setError('')
    try {
      await addAptLine({
        apartment: row.request.apartment,
        item_name: row.item_name,
        item_type: row.item_type || 'goods',
        category: row.category,
        vendor: row.vendor,
        centre_label: row.centre_label || 'Corporate',
        qty: row.qty,
        ppp: row.ppp,
        gst_rate: row.gst_rate,
        amount_inclusive: row.amount_inclusive,
        invoice_amount: row.invoice_amount,
        sides_per_file: row.sides_per_file,
        print_specification: row.print_specification,
        paper_kind: row.paper_kind,
        reference_id: row.reference_id,
        reference_link: row.reference_link,
        file_link: row.file_link,
        source_of_request: row.source_of_request,
        remarks: row.remarks
      })
      load()
    } catch (e) {
      setError(`Could not copy the line — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  const submitDraft = async (uuid) => {
    setBusy(true)
    try {
      await aptRequestAction(uuid, 'submit')
      load()
    } finally {
      setBusy(false)
    }
  }

  // Take one item off a draft from the open-drafts list.
  const removeDraftLine = async (line) => {
    setBusy(true)
    try {
      await deleteAptLine(line.uuid)
      load()
    } catch (e) {
      setError(`Could not remove the item — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // The At Apartment team's two responses to an HQ-removed line.
  const resendRemoved = async (uuid) => {
    setBusy(true)
    try {
      await resendAptLine(uuid)
      setRemovedInfo(false)
      load()
    } finally {
      setBusy(false)
    }
  }
  const clarifyRemoved = async (uuid) => {
    setBusy(true)
    try {
      await clarifyAptLine(uuid)
      setRemovedInfo(false)
      load()
    } finally {
      setBusy(false)
    }
  }

  // Unlock an approved list so the At Apartment team can fix it (2026-07-22).
  // It drops back to draft and has to go through approval again, so the caller
  // confirms that in the dialog below before it happens.
  const reopenRequest = (req) => setReopenReq(req)
  const confirmReopen = async () => {
    setBusy(true)
    try {
      await aptRequestAction(reopenReq.uuid, 'reopen')
      setReopenReq(null)
      load()
    } finally {
      setBusy(false)
    }
  }

  // Drag a line to a new spot in its own request (2026-07-22 ask). The order we
  // send covers the request's WHOLE line list, not just the filtered rows on
  // screen, so reordering under a filter can never drop the hidden ones.
  const dropOnRow = async (target) => {
    const src = dragUuid
    setDragUuid(null)
    if (!src || src === target.uuid) return
    const reqUuid = target.request.uuid
    const siblings = allRows.filter((r) => r.request.uuid === reqUuid)
    // Only within one request — Sl No. is per request, so moving a line across
    // requests would mean nothing.
    if (!siblings.some((r) => r.uuid === src)) return
    const order = siblings.map((r) => r.uuid).filter((u) => u !== src)
    const at = order.indexOf(target.uuid)
    order.splice(at < 0 ? order.length : at, 0, src)
    setBusy(true)
    try {
      await reorderAptLines(reqUuid, order)
      load()
    } finally {
      setBusy(false)
    }
  }

  const openDetail = async (uuid) => setDetail(await fetchAptRequest(uuid))
  const refreshDetail = async () => {
    if (detail) setDetail(await fetchAptRequest(detail.uuid))
    load()
  }

  // Bulk-add every At Apartment catalogue item in a category to the draft.
  const runBulkCat = async () => {
    if (!bulkCat.apartment || !bulkCat.category) {
      setBulkCat((b) => ({ ...b, error: 'Pick an apartment and a category.' }))
      return
    }
    const sets = Math.floor(
      Number(bulkCat.sets === undefined || bulkCat.sets === '' ? 1 : bulkCat.sets)
    )
    if (!Number.isFinite(sets) || sets < 1) {
      setBulkCat((b) => ({
        ...b,
        error: 'Number of sets must be a whole number of at least 1.'
      }))
      return
    }
    setBusy(true)
    try {
      const created = await bulkAddAptCategory({
        apartment: bulkCat.apartment,
        category: bulkCat.category,
        sets
      })
      setBulkCat(null)
      load()
      setDetail(created)
    } catch (err) {
      setBulkCat((b) => ({
        ...b,
        error:
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.response && err.response.data && err.response.data.detail) ||
          (err && err.message) ||
          'Could not add the category. Try again.'
      }))
    } finally {
      setBusy(false)
    }
  }

  // Vendor picker: approved vendors only (P4). "Manage vendors…" opens the
  // master where LXD proposes and HQ approves.
  const vendorSelect = (current, onPick) => {
    const opts =
      current && !vendorNames.includes(current)
        ? [current, ...vendorNames]
        : vendorNames
    return (
      <Select
        value={current || ''}
        displayEmpty
        style={{ fontSize: 13 }}
        onChange={(e) => {
          const v = e.target.value
          if (v === MANAGE_VENDORS) {
            setVendorMgr(true)
            return
          }
          onPick(v)
        }}>
        <MenuItem value="">—</MenuItem>
        {opts.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
        <MenuItem value={MANAGE_VENDORS}>+ Manage vendors…</MenuItem>
      </Select>
    )
  }

  const editCell = (row, col) => {
    if (col.kind === 'vendor') {
      return vendorSelect(row.vendor, (v) => saveCell(row, 'vendor', v))
    }
    if (col.kind === 'itemtype') {
      return (
        <Select
          value={row.item_type || 'goods'}
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, 'item_type', e.target.value)}>
          {ITEM_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (col.kind === 'bool') {
      return (
        <Select
          value={row.amount_inclusive ? 'yes' : 'no'}
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, 'amount_inclusive', e.target.value === 'yes')}>
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </Select>
      )
    }
    if (col.kind === 'select') {
      return (
        <Select
          value={row[col.k] || ''}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) =>
            e.target.value === NEW_CATEGORY
              ? askNewCategory((name) => saveCell(row, col.k, name))
              : saveCell(row, col.k, e.target.value)
          }>
          <MenuItem value="">—</MenuItem>
          {col.options.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
          {col.addNew && <NewCategoryMenuItem value={NEW_CATEGORY} />}
        </Select>
      )
    }
    if (col.kind === 'date') {
      return (
        <DateCell
          key={`${row.uuid}-${col.k}-${row.modified || ''}`}
          value={dateOf(row)}
          editable
          width={col.width}
          onCommit={(v) => saveCell(row, col.k, v)}
        />
      )
    }
    return (
      <TextField
        key={`${row.uuid}-${col.k}-${row.modified || ''}`}
        className={classes.editInput}
        type={col.kind === 'num' ? 'number' : 'text'}
        defaultValue={row[col.k] ?? ''}
        style={{ width: col.width || 90 }}
        onBlur={(e) => saveCell(row, col.k, e.target.value)}
      />
    )
  }

  const displayCell = (row, col) => {
    const v = row[col.k]
    if (col.kind === 'approval') {
      const r = row.request || {}
      const needsNo = r.approval_needed === 'no'
      const signed = [r.approved_by_name, r.approval_source].filter(Boolean).join(' · ')
      const label = needsNo
        ? 'No approval needed'
        : signed || (r.approval_needed === 'yes' ? 'Needs approval' : 'Set approval')
      const filled = needsNo || !!signed
      return (
        <Chip
          size="small"
          variant={filled ? 'default' : 'outlined'}
          label={label}
          onClick={(e) => openApproval(row.request, e.currentTarget)}
          title={r.approval_reference ? `Ref: ${r.approval_reference}` : 'Set approval'}
          style={{
            height: 20,
            fontSize: 11,
            cursor: 'pointer',
            maxWidth: 150,
            color: filled ? '#fff' : undefined,
            background: needsNo ? '#607d8b' : signed ? '#2e7d32' : undefined
          }}
        />
      )
    }
    if (col.k === 'status') {
      // A removed/clarification/re-sent line must NOT keep reading "handed over".
      if (row.removed) {
        const clar = row.clarification_requested
        return (
          <Chip
            size="small"
            label={
              clar
                ? 'Clarification requested'
                : row.fulfilment_status === 'delivered'
                ? 'Removed by HQ · after delivery'
                : 'Removed by HQ'
            }
            onClick={() => setRemovedInfo(row)}
            style={{
              cursor: 'pointer',
              color: '#fff',
              height: 20,
              fontSize: 11,
              background: clar ? '#ef6c00' : '#c62828'
            }}
          />
        )
      }
      if (row.resent_by_at_apartment) {
        return (
          <Chip
            size="small"
            label="Re-sent to HQ"
            title="You re-sent this to HQ to procure again"
            style={{ color: '#fff', height: 20, fontSize: 11, background: '#1565c0' }}
          />
        )
      }
      return (
        <StageCell
          row={row}
          disabled={busy}
          onTrack={setTrackReq}
          onPick={pickDispatch}
        />
      )
    }
    if (col.kind === 'money') {
      return Number(v) ? money(v) : '—'
    }
    if (col.kind === 'date') {
      return fmtDate(dateOf(row)) || '—'
    }
    if (col.k === 'total_sides') {
      return v ? Number(v) : '—'
    }
    if (col.kind === 'link') {
      return v ? (
        <Link href={v} target="_blank" rel="noopener noreferrer">
          {col.k === 'file_link' ? 'file' : 'link'}
        </Link>
      ) : (
        '—'
      )
    }
    if (col.k === 'sl_no') {
      return v
    }
    if (col.kind === 'at_apartment') {
      // This grid is always At Apartment requests (origin=apartment).
      return row.request && row.request.apartment ? 'Yes' : '—'
    }
    if (col.kind === 'centre') {
      // Show the apartment it was raised for, not the default centre label.
      return row.apartment_name || v || '—'
    }
    if (col.k === 'remarks') {
      return row.remarks || row.receipt_note || '—'
    }
    return v === '' || v == null ? '—' : String(v)
  }

  // One data cell: dead print columns on goods rows show a muted dash; the
  // invoice cell is editable only on GST-inclusive lines; otherwise the usual
  // editable-vs-display split.
  const renderDataCell = (row, col) => {
    // Product carries the line's link now that the File/Link column is gone.
    // The name itself behaves exactly as it did — inline TextField on a draft,
    // plain text once locked — the arrow just rides beside it. `minWidth: 0` is
    // load-bearing: Product is a PINNED column held to 190px, and without it a
    // long name pushes the arrow out of the frozen cell.
    if (col.k === 'item_name' && !row.removed) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {row.editable ? editCell(row, col) : displayCell(row, col)}
          </span>
          <LineLink
            row={row}
            editable={!!row.editable}
            onSave={(r, v) => saveCell(r, 'reference_link', v)}
          />
          {/* The second file (the back of a two-PDF print item, 2026-09-02)
              was saved on the line but never shown here (Satya, 2026-09-17:
              "i still can't see 2 links in 1 item"). */}
          {row.file_link && (
            <IconButton
              size="small"
              color="primary"
              disableRipple
              style={{
                padding: 0,
                marginLeft: 2,
                flex: '0 0 auto',
                backgroundColor: 'transparent'
              }}
              title={`Second file: ${row.file_link}`}
              onClick={() =>
                window.open(row.file_link, '_blank', 'noopener,noreferrer')
              }>
              <CallMadeIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </span>
      )
    }
    // Approval is a click-to-edit popup chip, never an inline field.
    if (col.kind === 'approval') return displayCell(row, col)
    if (cellDead(row, col)) {
      return <span style={{ color: '#bbb' }}>—</span>
    }
    if (col.k === 'invoice_amount') {
      if (row.editable && invoiceEditable(row)) {
        return (
          <TextField
            key={`${row.uuid}-inv-${row.modified || ''}`}
            className={classes.editInput}
            type="number"
            defaultValue={row.invoice_amount ?? ''}
            style={{ width: 90 }}
            onBlur={(e) => saveCell(row, 'invoice_amount', e.target.value)}
          />
        )
      }
      return displayCell(row, col)
    }
    // At Apartment + Center/Apartment are the request's apartment (set once at
    // creation), not a per-line editable centre — always show it, never an
    // editable 'Corporate' default.
    if (col.kind === 'centre' || col.kind === 'at_apartment') {
      return displayCell(row, col)
    }
    // Remarks stay editable through fulfilment so a shortfall / discrepancy can
    // be noted when the goods are received, even on an otherwise-locked line.
    if (
      col.k === 'remarks' &&
      (row.editable ||
        ['final_approved', 'in_daily', 'handed_over'].includes(row.status))
    ) {
      return editCell(row, col)
    }
    if (
      row.editable &&
      col.kind &&
      !['calc', 'money', 'dispatch'].includes(col.kind)
    ) {
      return col.kind === 'link'
        ? editCell(row, { ...col, kind: 'text' })
        : editCell(row, col)
    }
    return displayCell(row, col)
  }

  // One cell of the new-line entry row. Mirrors renderDataCell but writes to
  // the local `entry` object instead of saving.
  // Fill the entry row from a picked catalogue item (parity with the daily
  // sheet). Only overwrite fields the catalogue carries; all stay editable.
  const pickCatalogue = (it, setEntry) => {
    setEntry((p) => ({
      ...p,
      item_name: it.item_name || it.name || p.item_name,
      ppp: it.ppp != null && it.ppp !== '' ? it.ppp : p.ppp,
      reference_link: it.reference_link || p.reference_link,
      reference_id: it.reference_id || p.reference_id,
      // The grid's own heads ("5+ Robotics"), the same list as its Category
      // column — not the HQ daily sheet's At Apartment list ("5-12 Robotics").
      category: matchAptCategory(it.category) || p.category,
      unit: it.unit || p.unit,
      vendor: it.vendor || p.vendor,
      // ⭐⭐ segment AND the second artwork file ride along too (2026-09-02).
      // Every pick path copied vendor but not these: a hand-added line landed
      // blank-segment (so it grouped nowhere, quietly undoing the 276-row
      // backfill) and with only the front PDF. The backend's bulk payload has
      // carried segment since #737 — only these four FE paths were left behind.
      segment: it.segment || p.segment,
      file_link: it.file_link || p.file_link,
      ...printFieldsFrom(it)
    }))
  }

  // Renders one entry cell for a given draft (`entry`/`setEntry` are that
  // draft's slice — the grid keeps a list of drafts now).
  const renderEntryCell = (c, entry, setEntry) => {
    if (c.kind === 'approval') {
      const label = entryApprovalSet
        ? [entryApproval.approved_by_name, entryApproval.approval_source]
            .filter(Boolean)
            .join(' · ') ||
          (entryApproval.approval_needed === 'no' ? 'No approval needed' : 'Approval set')
        : 'Set approval'
      return (
        <Chip
          size="small"
          variant={entryApprovalSet ? 'default' : 'outlined'}
          label={label}
          onClick={(e) => openApproval(null, e.currentTarget)}
          title="Sign-off is applied to the request once the line is saved"
          style={{
            height: 20,
            fontSize: 11,
            cursor: 'pointer',
            maxWidth: 150,
            color: entryApprovalSet ? '#fff' : undefined,
            background: entryApprovalSet ? '#2e7d32' : undefined
          }}
        />
      )
    }
    if (c.k === 'item_name') {
      return (
        <AptCatalogueField
          value={entry.item_name}
          width={c.width}
          onType={(v) => setEntry((p) => ({ ...p, item_name: v }))}
          onPick={(it) => pickCatalogue(it, setEntry)}
        />
      )
    }
    if (c.k === 'apartment_name') {
      return (
        <Select
          value={entryApt}
          style={{ fontSize: 13 }}
          onChange={(e) => setEntryApt(e.target.value)}>
          {apartments.map((a) => (
            <MenuItem key={a.uuid} value={a.uuid}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (c.k === 'po_number') {
      // Minted server-side when the request is created, so there is nothing to
      // type here — say so rather than leaving the cell blank.
      return <span style={{ color: '#999' }}>auto</span>
    }
    if (c.k === 'requested_by_name') {
      return <Typography variant="caption">new line</Typography>
    }
    if (c.k === 'status') {
      return <Typography variant="caption">draft</Typography>
    }
    if (cellDead({ item_type: entry.item_type }, c)) {
      return <span style={{ color: '#bbb' }}>—</span>
    }
    if (c.kind === 'vendor') {
      return vendorSelect(entry.vendor, (v) =>
        setEntry((p) => ({ ...p, vendor: v }))
      )
    }
    if (c.kind === 'itemtype') {
      return (
        <Select
          value={entry.item_type || 'goods'}
          style={{ fontSize: 13 }}
          onChange={(e) => {
            const v = e.target.value
            setEntry((p) => ({ ...p, item_type: v }))
          }}>
          {ITEM_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (c.kind === 'bool') {
      return (
        <Select
          value={entry.amount_inclusive ? 'yes' : 'no'}
          style={{ fontSize: 13 }}
          onChange={(e) => {
            const yes = e.target.value === 'yes'
            setEntry((p) => ({ ...p, amount_inclusive: yes }))
          }}>
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </Select>
      )
    }
    if (c.k === 'invoice_amount') {
      if (!entry.amount_inclusive) return <span style={{ color: '#bbb' }}>—</span>
      return (
        <TextField
          className={classes.editInput}
          type="number"
          placeholder="Invoice"
          style={{ width: 90 }}
          value={entry.invoice_amount ?? ''}
          onChange={(e) => {
            const v = e.target.value
            setEntry((p) => ({ ...p, invoice_amount: v }))
          }}
        />
      )
    }
    if (c.kind === 'select') {
      return (
        <Select
          value={entry[c.k] || ''}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => {
            const v = e.target.value
            if (v === NEW_CATEGORY) {
              askNewCategory((name) => setEntry((p) => ({ ...p, [c.k]: name })))
              return
            }
            setEntry((p) => ({ ...p, [c.k]: v }))
          }}>
          <MenuItem value="">—</MenuItem>
          {c.options.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
          {c.addNew && <NewCategoryMenuItem value={NEW_CATEGORY} />}
        </Select>
      )
    }
    if (c.kind === 'date') {
      return (
        <DateCell
          value={entry[c.k] ?? ''}
          editable
          width={c.width}
          onChange={(v) => setEntry((p) => ({ ...p, [c.k]: v }))}
        />
      )
    }
    if (c.kind === 'text' || c.kind === 'num' || c.kind === 'link') {
      return (
        <TextField
          className={classes.editInput}
          type={c.kind === 'num' ? 'number' : 'text'}
          placeholder={c.label}
          style={{ width: c.width || 90 }}
          value={entry[c.k] ?? ''}
          onChange={(e) => {
            const v = e.target.value
            setEntry((p) => ({ ...p, [c.k]: v }))
          }}
        />
      )
    }
    // A new line is always an At Apartment line, and its apartment is picked
    // here (Satya, 2026-09-17: "not able to choose the apartment from here").
    // One choice applies to every draft row, like before.
    if (c.kind === 'at_apartment') {
      return <Typography variant="caption">Yes</Typography>
    }
    if (c.kind === 'centre') {
      return (
        <Select
          value={entryApt}
          displayEmpty
          style={{
            fontSize: 13,
            minWidth: 140,
            color: entryApt ? undefined : '#B84427'
          }}
          onChange={(e) => {
            const v = e.target.value
            setEntryApt(v)
          }}>
          <MenuItem value="" disabled>
            Pick apartment
          </MenuItem>
          {apartments.map((a) => (
            <MenuItem key={a.uuid} value={a.uuid}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (c.k === 'sl_no') {
      return <span className={classes.entryLabel}>new</span>
    }
    return '—'
  }

  // One cell of the column-aligned bulk-edit row (ported). Returns null for
  // columns that can't be bulk-set, so those cells stay empty and everything
  // lines up under the header.
  const renderBulkCell = (c) => {
    const k = c.db || c.k
    const val = bulkValues[k] ?? ''
    if (c.k === 'item_name') {
      return (
        <AptCatalogueField
          value={bulkValues.item_name || ''}
          width={c.width}
          onType={(v) => setBulkVal('item_name', v)}
          onPick={pickBulkCatalogue}
        />
      )
    }
    if (c.kind === 'vendor') return vendorSelect(val, (v) => setBulkVal(k, v))
    if (c.kind === 'itemtype') {
      return (
        <Select value={val} displayEmpty style={{ fontSize: 13 }}
          onChange={(e) => setBulkVal(k, e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {ITEM_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      )
    }
    if (c.kind === 'select') {
      return (
        <Select value={val} displayEmpty style={{ fontSize: 13 }}
          onChange={(e) =>
            e.target.value === NEW_CATEGORY
              ? askNewCategory((name) => setBulkVal(k, name))
              : setBulkVal(k, e.target.value)
          }>
          <MenuItem value="">—</MenuItem>
          {(c.options || []).map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
          {c.addNew && <NewCategoryMenuItem value={NEW_CATEGORY} />}
        </Select>
      )
    }
    if (c.kind === 'text' || c.kind === 'num' || c.kind === 'date' || c.kind === 'link') {
      return (
        <TextField
          className={classes.editInput}
          // eslint-disable-next-line no-nested-ternary
          type={c.kind === 'num' ? 'number' : c.kind === 'date' ? 'date' : 'text'}
          placeholder={c.label}
          style={{ width: c.width || 110 }}
          value={val}
          onChange={(e) => setBulkVal(k, e.target.value)}
        />
      )
    }
    return null
  }

  const canWrite = role.can_request || role.is_central


  return (
    <Box>
      {(() => {
        const actions = (
          <>
            {canWrite && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                disableElevation
                onClick={addLineFromTop}>
                + Add line
              </Button>
            )}
            <Typography variant="caption" color="textSecondary">
              {rows.length} line{rows.length === 1 ? '' : 's'}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
              Taxable {money(totals.taxable)} · GST {money(totals.gst)} · Invoice{' '}
              {money(totals.invoice)}
            </Typography>
            <Box style={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setActionsAnchor(e.currentTarget)}>
              Actions ▾
            </Button>
          </>
        )
        // One toolbar row: primary + Add line, live counts/totals, and a single
        // Actions menu grouping add / import / export / manage (2026-07-25).
        return <Box className={classes.bar}>{actions}</Box>
      })()}

      <Menu
        anchorEl={actionsAnchor}
        open={!!actionsAnchor}
        onClose={() => setActionsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {canWrite && <ListSubheader disableSticky>Add lines</ListSubheader>}
        {canWrite && (
          <MenuItem
            onClick={() => {
              setBulkCat({
                apartment: entryApt || (apartments[0] && apartments[0].uuid),
                category: APT_CATEGORIES[0],
                sets: '1',
                error: ''
              })
              setActionsAnchor(null)
            }}>
            Add category
          </MenuItem>
        )}
        {canWrite && (
          <MenuItem
            onClick={() => {
              setBulk({
                apartment: entryApt || (apartments[0] && apartments[0].uuid),
                rows: null,
                fileName: ''
              })
              setActionsAnchor(null)
            }}>
            Bulk upload (CSV)
          </MenuItem>
        )}
        {canWrite && <Divider />}
        <ListSubheader disableSticky>Export</ListSubheader>
        <MenuItem onClick={() => runExport('excel')}>Excel</MenuItem>
        <MenuItem onClick={() => runExport('pdf')}>PDF</MenuItem>
        <Divider />
        <ListSubheader disableSticky>Manage</ListSubheader>
        <MenuItem
          onClick={() => {
            setVendorMgr(true)
            setActionsAnchor(null)
          }}>
          Vendors
        </MenuItem>
      </Menu>

      {/* Day navigation + period filter, ported from the daily sheet. */}
      <Box className={classes.bar}>
        <Box style={{ flex: 1 }} />
        <Button
          size="small"
          variant={day === todayStr() ? 'contained' : 'outlined'}
          color={day === todayStr() ? 'primary' : 'default'}
          disableElevation
          title="Jump to today"
          onClick={() => setDay(todayStr())}>
          Today
        </Button>
        <Button size="small" onClick={() => shiftDay(-1)} title="Previous day">
          ◀
        </Button>
        <TextField
          type="date"
          size="small"
          title="Go to a date"
          className={classes.editInput}
          style={{ width: 140 }}
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
        <Button size="small" onClick={() => shiftDay(1)} title="Next day">
          ▶
        </Button>
        {day ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setDay('')}
            title="Clear the date filter and list every day">
            Show all days
          </Button>
        ) : (
          <Typography variant="caption" color="textSecondary">
            showing all days
          </Typography>
        )}
        <Button
          size="small"
          variant={periodActive ? 'contained' : 'outlined'}
          color={periodActive ? 'primary' : 'default'}
          disableElevation
          onClick={(e) => setPeriodAnchor(e.currentTarget)}>
          📅 Period: {periodLabel}
        </Button>
      </Box>

      <Popover
        open={!!periodAnchor}
        anchorEl={periodAnchor}
        onClose={() => setPeriodAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box style={{ padding: 16, width: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Box>
            <Typography variant="caption" color="textSecondary">Financial year</Typography>
            <Select value={fy} displayEmpty fullWidth style={{ fontSize: 13 }}
              onChange={(e) => setFy(e.target.value)}>
              <MenuItem value="">All FYs</MenuItem>
              {fyList.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Month</Typography>
            <Select value={month} displayEmpty fullWidth style={{ fontSize: 13 }}
              onChange={(e) => setMonth(e.target.value)}>
              <MenuItem value="">Any month</MenuItem>
              {months.map((m) => (
                <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Custom range</Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TextField type="date" size="small" title="From date"
                className={classes.editInput} style={{ flex: 1 }}
                value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <Typography variant="caption">–</Typography>
              <TextField type="date" size="small" title="To date"
                className={classes.editInput} style={{ flex: 1 }}
                value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Box>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button size="small" onClick={clearPeriod} disabled={!periodActive}>
              Clear
            </Button>
            <Button size="small" color="primary" onClick={() => setPeriodAnchor(null)}>
              Done
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* One button, not a chip per draft: the drafts are listed, with their
          items, in the pop-up it opens — same list HQ reviews orders in. */}
      {drafts.length > 0 && (
        <Box className={classes.draftChipBar}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => setDraftsOpen(true)}>
            {`Open drafts · ${drafts.length}`}
          </Button>
        </Box>
      )}
      <PendingOrdersDialog
        kind={draftsOpen ? 'submit' : null}
        requests={drafts}
        liveLines={(r) => r.lines || []}
        busy={busy}
        onAct={(r) => submitDraft(r.uuid)}
        onRemoveLine={removeDraftLine}
        onClose={() => setDraftsOpen(false)}
      />

      {error && (
        <Box
          style={{
            margin: '0 0 8px',
            padding: '8px 12px',
            border: '1px solid #E4CFC6',
            background: '#FBEDE8',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
          <Typography variant="body2" style={{ color: '#B84427', flex: 1 }}>
            {error}
          </Typography>
          <Button size="small" onClick={() => setError('')}>
            Dismiss
          </Button>
        </Box>
      )}

      {rows.some((r) => r.status === 'submitted') && (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ marginBottom: 8 }}>
          Some of these lines are already with the cluster manager. You can still
          edit them — the request drops back to draft and has to be submitted
          again.
        </Typography>
      )}

      {rows.length > 0 && !editableRows.length && (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ marginBottom: 8 }}>
          {rows.some((r) => r.reopenable)
            ? 'These lines are approved, so they are locked. The At Apartment head or Procurement HQ can reopen the request (🔓 at the end of a row) if something needs changing.'
            : 'These lines are locked — the request has moved past editing. Procurement HQ handles changes from here.'}
        </Typography>
      )}

      {/* Column views + band folding (2026-08-26). Twenty-five columns is more
          than any one job needs; a view sets which bands are folded, and
          touching a band afterwards just drops you to a custom arrangement. */}
      {/* The Ordering/Receiving/Finance column presets stay an HQ daily-sheet
          thing — they name HQ's jobs, which read as noise to the LXD team
          (Satya, 2026-08-31). Folding here is by the orange band headers. */}
      <Box className={classes.bar} style={{ marginBottom: 8 }}>
        <Button
          size="small"
          className={classes.bandChip}
          variant={grouped ? 'contained' : 'outlined'}
          color={grouped ? 'primary' : 'default'}
          disableElevation
          title="Group the lines by segment — the catalogue's own grouping, the way the games board groups by game"
          onClick={toggleGrouped}>
          Group by segment
        </Button>
        {grouped && (
          <>
            <Button size="small" className={classes.bandChip} onClick={expandAll}>
              Expand all
            </Button>
            <Button
              size="small"
              className={classes.bandChip}
              onClick={() => collapseAllOf(rows)}>
              Collapse all
            </Button>
          </>
        )}
      </Box>

      {selectedEditable.length > 0 && (
        <Box
          className={classes.bar}
          style={{ background: '#eef4ff', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <Typography variant="body2">
            <b>{selectedEditable.length}</b> selected
          </Typography>
          <Typography variant="caption" color="textSecondary">
            fill the highlighted row below, then Apply
          </Typography>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy || !bulkHasValues}
            onClick={applyBulkRow}>
            Apply to {selectedEditable.length}
          </Button>
          <span style={{ borderLeft: '1px solid #c7d5ee', height: 20 }} />
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={(e) => openBulkApproval(e.currentTarget)}>
            Set approval…
          </Button>
          <Button
            size="small"
            style={{ color: '#c62828' }}
            disabled={busy}
            onClick={() => setConfirmDel(true)}>
            Delete {selectedEditable.length}
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Box>
      )}

      {selectedRemoved.length > 0 && (
        <Box
          className={classes.bar}
          style={{ background: '#fdecea', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <Typography variant="body2">
            <b>{selectedRemoved.length}</b> removed line
            {selectedRemoved.length === 1 ? '' : 's'} selected
          </Typography>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={resendSelectedRemoved}>
            Re-send {selectedRemoved.length} to procure
          </Button>
          <Button
            size="small"
            style={{ color: '#c62828' }}
            disabled={busy}
            onClick={() => setConfirmCancel(true)}>
            Cancel {selectedRemoved.length}
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Box>
      )}

      {selectedReopenable.length > 0 && (
        <Box
          className={classes.bar}
          style={{ background: '#fff3e0', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <Typography variant="body2">
            <b>{selectedReopenable.length}</b> approved line
            {selectedReopenable.length === 1 ? '' : 's'} selected
            {reopenReqCount > 1 ? ` · ${reopenReqCount} requests` : ''}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            reopening sends the whole request back to draft for editing
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => setConfirmReopenBulk(true)}>
            Reopen {reopenReqCount} request{reopenReqCount === 1 ? '' : 's'}
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Box>
      )}

      {selectedReceivable.length > 0 && (
        <Box
          className={classes.bar}
          style={{ background: '#e8f5e9', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <Typography variant="body2">
            <b>{selectedReceivable.length}</b> line
            {selectedReceivable.length === 1 ? '' : 's'} selected
          </Typography>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={markReceivedByHq}>
            Mark received by HQ procurement team
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Box>
      )}

      <Paper variant="outlined" className={classes.scroller}>
        <Table size="small" className={classes.table}>
          <TableHead>
            {/* Band header. Each band spans its own contiguous run; clicking it
                folds the run to a single stub. Ungrouped columns sit under a
                blank spacer so the row still spans the whole table. */}
            <TableRow className={classes.bandRow}>
              <TableCell padding="none" className={classes.bandRowSpacer} />
              <TableCell padding="checkbox" className={classes.bandRowSpacer} />
              {bandHeaderCells}
              <TableCell className={classes.bandRowSpacer} />
            </TableRow>
            <TableRow>
              <TableCell padding="none" className={classes.filterHeadCell} />
              <TableCell padding="checkbox" className={classes.filterHeadCell}>
                <Checkbox
                  size="small"
                  checked={allSelectableSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              {mapGridCells((c) => {
                const pin = pinProps(c, classes.filterHeadCell)
                return (
                <TableCell
                  key={c.k}
                  className={[pin.className, classes.pinnedHead]
                    .filter(Boolean)
                    .join(' ')}
                  // Without the same cap the header would set the column width
                  // straight back to what the body cells stopped asking for.
                  style={
                    pin.style ||
                    (c.wrap
                      ? { width: c.wrap, minWidth: c.wrap, maxWidth: c.wrap }
                      : undefined)
                  }
                >
                  <Tooltip title={c.tip || ''} arrow>
                    {c.filter ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        className={classes.filterInput}
                        placeholder={c.label}
                        style={{ width: Math.max(c.width || 0, 90) }}
                        value={filters[c.k] || ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setFilters((p) => ({ ...p, [c.k]: v }))
                        }}
                      />
                    ) : (
                      <span>{c.label}</span>
                    )}
                  </Tooltip>
                </TableCell>
                )
              })}
              <TableCell className={classes.filterHeadCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {entryOpen &&
              canWrite &&
              entryRows.map((entry, di) => {
                const setEntry = (updater) =>
                  setEntryRows((ds) =>
                    ds.map((d, j) =>
                      j === di
                        ? typeof updater === 'function'
                          ? updater(d)
                          : updater
                        : d
                    )
                  )
                // Only the bottom entry row carries the rule that separates the
                // whole strip from the saved lines below it.
                const isLastEntry = di === entryRows.length - 1
                return (
                  <TableRow
                    key={di}
                    ref={isLastEntry ? entryRowRef : undefined}
                    data-entry-index={di}
                    className={
                      isLastEntry
                        ? `${classes.entryRow} ${classes.entryRowLast}`
                        : classes.entryRow
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        commitDraft(entry)
                      }
                    }}
                    onBlur={(e) => {
                      const row = e.currentTarget
                      window.setTimeout(() => {
                        if (row.isConnected && row.contains(document.activeElement)) return
                        if (document.querySelector('.MuiPopover-root, .MuiMenu-root'))
                          return
                        if (!busy && draftReady(entry)) commitDraft(entry, { silent: true })
                      }, 120)
                    }}>
                    <TableCell padding="none" className={classes.cell} />
                    <TableCell padding="checkbox" className={classes.cell} />
                    {mapGridCells((c) => {
                      const pin = pinProps(c, classes.cell)
                      return (
                        <TableCell
                          key={c.k}
                          className={pin.className}
                          style={pin.style}>
                          {renderEntryCell(c, entry, setEntry)}
                        </TableCell>
                      )
                    })}
                    {/* A visible Save (2026-09-17). Saving used to happen only on
                        Enter or when focus left the row — with no button and
                        nothing said when a field was missing, so a line that
                        could not save just sat there. */}
                    <TableCell className={classes.cell}>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 4 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disableElevation
                          disabled={busy || !draftReady(entry)}
                          onClick={() => commitDraft(entry)}>
                          Save
                        </Button>
                        <IconButton
                          size="small"
                          title="Remove this draft line"
                          onClick={() => removeDraft(entry)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {draftStarted(entry) && !draftReady(entry) && (
                        <Typography
                          variant="caption"
                          component="div"
                          style={{ color: '#B84427', whiteSpace: 'nowrap' }}>
                          {`Needs ${draftMissing(entry).join(', ')}`}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}

            {/* Column-aligned bulk-edit row: fill any column, Apply, and every
                ticked row gets it (ported from the daily sheet). */}
            {selected.size > 0 && (
              <TableRow style={{ background: '#fff7e0' }}>
                <TableCell padding="none" className={classes.cell} />
                <TableCell padding="checkbox" className={classes.cell}>
                  <Tooltip title="Bulk fill the ticked rows" arrow>
                    <span style={{ fontSize: 16 }}>✎</span>
                  </Tooltip>
                </TableCell>
                {mapGridCells((c) => {
                  const pin = pinProps(c, classes.cell)
                  return (
                    <TableCell
                      key={c.k}
                      className={pin.className}
                      style={pin.style}>
                      {renderBulkCell(c)}
                    </TableCell>
                  )
                })}
                <TableCell className={classes.cell}>
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={busy || !bulkHasValues}
                    onClick={applyBulkRow}>
                    Apply
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {groupEntries(rows, grouped, collapsedGroups).map((entry) =>
              entry.kind === 'group' ? (
                <TableRow
                  key={`grp-${entry.key}`}
                  className={classes.groupRow}
                  onClick={() => toggleGroup(entry.key)}>
                  <TableCell padding="none" className={classes.cell} />
                  <TableCell padding="checkbox" className={classes.cell} />
                  {mapGridCells((c) => {
                    const pin = pinProps(c, classes.cell)
                    // The segment name rides in the Product column so it stays
                    // put when the grid is scrolled right — the same column
                    // that is pinned for the rows underneath it.
                    const body =
                      c.k === 'sl_no' ? (
                        <span style={{ color: '#8a857c', fontSize: 11 }}>
                          {entry.open ? '▾' : '▸'}
                        </span>
                      ) : c.k === 'item_name' ? (
                        <>
                          {entry.name}
                          <span className={classes.groupMeta}>
                            {entry.count} item{entry.count === 1 ? '' : 's'}
                          </span>
                        </>
                      ) : null
                    return (
                      <TableCell
                        key={c.k}
                        className={pin.className}
                        style={pin.style}>
                        {body}
                      </TableCell>
                    )
                  })}
                  <TableCell className={classes.cell} />
                </TableRow>
              ) : (
                ((row) => (
              <TableRow
                key={row.uuid}
                hover
                className={classes.tableRow}
                onDragOver={dragUuid ? (e) => e.preventDefault() : undefined}
                onDrop={dragUuid ? () => dropOnRow(row) : undefined}
                style={
                  row.removed
                    ? { opacity: 0.55, textDecoration: 'line-through' }
                    : dragUuid === row.uuid
                    ? { opacity: 0.4 }
                    : undefined
                }>
                <TableCell padding="none" className={classes.cell}>
                  {row.editable && (
                    <Tooltip title="Drag to move this line up or down" arrow>
                      <span
                        draggable
                        onDragStart={() => setDragUuid(row.uuid)}
                        onDragEnd={() => setDragUuid(null)}
                        style={{
                          cursor: 'grab',
                          display: 'inline-flex',
                          color: '#9e9e9e'
                        }}>
                        <DragIndicatorIcon fontSize="small" />
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell padding="checkbox" className={classes.cell}>
                  {row.removed ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={selected.has(row.uuid)}
                        onChange={() => toggleSel(row.uuid)}
                      />
                      <Tooltip title="Removed by HQ — tap for options" arrow>
                        <IconButton
                          size="small"
                          style={{ color: '#c62828', textDecoration: 'none' }}
                          onClick={() => setRemovedInfo(row)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </span>
                  ) : (
                    <>
                  {(row.editable || row.reopenable || row.receivable) && (
                    <Checkbox
                      size="small"
                      checked={selected.has(row.uuid)}
                      onChange={() => toggleSel(row.uuid)}
                    />
                  )}
                  {row.sentToHq && !row.receivable && (
                    <Tooltip title="Sent to HQ for procurement — why can't I edit or delete this?" arrow>
                      <IconButton
                        size="small"
                        style={{ color: '#9e9e9e' }}
                        onClick={() => setLockedInfo(true)}>
                        <LockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                    </>
                  )}
                </TableCell>
                {mapGridCells((col) => {
                  const pin = pinProps(
                    col,
                    col.wrap
                      ? `${classes.cell} ${classes.wrapCell}`
                      : classes.cell
                  )
                  return (
                    <TableCell
                      key={col.k}
                      className={pin.className}
                      style={
                        pin.style ||
                        (col.wrap
                          ? {
                              width: col.wrap,
                              minWidth: col.wrap,
                              maxWidth: col.wrap
                            }
                          : undefined)
                      }
                    >
                      {renderDataCell(row, col)}
                    </TableCell>
                  )
                })}
                <TableCell className={classes.cell}>
                  {row.receivable && (
                    <IconButton
                      size="small"
                      title="Mark this item received by HQ procurement team"
                      disabled={busy}
                      style={{ color: '#2e7d32' }}
                      onClick={() => markLineReceived(row)}>
                      <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    title="Open request"
                    onClick={() => openDetail(row.request.uuid)}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                  {row.editable && (
                    <>
                      <IconButton
                        size="small"
                        title="Copy line"
                        disabled={busy}
                        onClick={() => copyLine(row)}>
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Delete line"
                        onClick={async () => {
                          await deleteAptLine(row.uuid)
                          load()
                        }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {row.reopenable && (
                    <IconButton
                      size="small"
                      title="Reopen for editing — sends the whole request back to draft, and it has to be approved again"
                      disabled={busy}
                      onClick={() => reopenRequest(row.request)}>
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
                ))(entry.row)
              )
            )}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={displayCols.length + 3} className={classes.cell}>
                  <Typography variant="body2" color="textSecondary">
                    No lines match — clear a filter, or add the first line above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {reopenReq && (
        <Dialog open onClose={() => setReopenReq(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Reopen for editing?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              <b>{reopenReq.title || 'This request'}</b> goes back to draft so the
              At Apartment team can change it. The approvals it already has are
              cleared, and it has to be submitted and approved again.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReopenReq(null)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={confirmReopen}>
              Reopen
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {lockedInfo && (
        <Dialog open onClose={() => setLockedInfo(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Already sent to HQ for procurement</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This item is on HQ's daily sheet — the HQ team has started procuring
              it, so it can't be edited or deleted from here.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 12 }}>
              To remove it, <b>ask the HQ team to stop procurement</b> for this
              item first; once they take it off their sheet you'll be able to
              delete it here.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={() => setLockedInfo(false)}>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {removedInfo && (
        <Dialog open onClose={() => setRemovedInfo(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Removed by the HQ team</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              HQ removed <b>{removedInfo.item_name || 'this item'}</b> from the
              daily sheet
              {removedInfo.fulfilment_status === 'delivered'
                ? ' after it was marked delivered'
                : ''}
              , so it won't be procured. Pick how you want to respond:
            </Typography>
            <Typography variant="body2" style={{ marginTop: 12 }}>
              • <b>Re-send to procure</b> — puts it back on HQ's daily sheet as a
              fresh line for them to order again.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6 }}>
              • <b>Ask HQ to clarify</b> — flags it back to HQ so they can explain
              why it was removed (useful when it was already marked delivered).
            </Typography>
            {removedInfo.clarification_requested && (
              <Typography
                variant="caption"
                style={{ color: '#ef6c00', display: 'block', marginTop: 10 }}>
                Clarification already requested — HQ can see this on their daily sheet.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemovedInfo(false)} disabled={busy}>
              Close
            </Button>
            <Button
              onClick={() => clarifyRemoved(removedInfo.uuid)}
              disabled={busy || removedInfo.clarification_requested}>
              Ask HQ to clarify
            </Button>
            <Button
              color="primary"
              variant="contained"
              disableElevation
              disabled={busy}
              onClick={() => resendRemoved(removedInfo.uuid)}>
              Re-send to procure
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {detail && (
        <RequestDetail
          request={detail}
          onClose={() => setDetail(null)}
          onChanged={refreshDetail}
        />
      )}

      {newCategoryDialog}

      {vendorMgr && (
        <VendorManager
          role={role}
          onClose={() => setVendorMgr(false)}
          onChanged={loadVendors}
        />
      )}

      {bulk && (
        <BulkUploadDialog
          bulk={bulk}
          setBulk={setBulk}
          apartments={apartments}
          vendorNames={vendorNames}
          onClose={() => setBulk(null)}
          onCreated={(created) => {
            setBulk(null)
            load()
            if (created) setDetail(created)
          }}
        />
      )}

      {bulkCat && (
        <Dialog open onClose={() => setBulkCat(null)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {bulkCat.category === GENERAL_GROUP
              ? 'Add the general items'
              : 'Add a category'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              Adds every At Apartment catalogue item in the chosen category to the
              apartment's draft — each line is seeded at the item's required qty
              from the catalogue (edit any in the grid before submitting). Print
              lines arrive with their specification, paper and sides already set.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Apartment"
              style={{ marginBottom: 12 }}
              value={bulkCat.apartment || ''}
              onChange={(e) => setBulkCat((b) => ({ ...b, apartment: e.target.value }))}>
              {apartments.map((a) => (
                <MenuItem key={a.uuid} value={a.uuid}>{a.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Category"
              value={bulkCat.category || ''}
              onChange={(e) => setBulkCat((b) => ({ ...b, category: e.target.value }))}>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
              {/* The sheet's general/common supplies carry no budget head, so
                  they are addressed by their catalogue grouping (2026-08-03). */}
              <MenuItem value={GENERAL_GROUP}>General items</MenuItem>
            </TextField>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              type="number"
              label="Number of sets"
              style={{ marginTop: 12 }}
              inputProps={{ min: 1, step: 1 }}
              value={bulkCat.sets === undefined ? '1' : bulkCat.sets}
              onChange={(e) => {
                const v = e.target.value
                setBulkCat((b) => ({ ...b, sets: v }))
              }}
              helperText="Each line arrives at its required qty × this many sets."
            />
            {bulkCat.error && (
              <Typography variant="caption" style={{ color: '#c62828' }}>
                {bulkCat.error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkCat(null)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disableElevation
              disabled={busy || !bulkCat.apartment || !bulkCat.category}
              onClick={runBulkCat}>
              Add all
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <TypedDeleteDialog
        open={confirmDel}
        count={selectedEditable.length}
        onClose={() => setConfirmDel(false)}
        onConfirm={deleteSelected}
      />

      <TypedDeleteDialog
        open={confirmCancel}
        count={selectedRemoved.length}
        onClose={() => setConfirmCancel(false)}
        onConfirm={cancelSelectedRemoved}
      />

      <Dialog open={confirmReopenBulk} onClose={() => setConfirmReopenBulk(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reopen {reopenReqCount} request{reopenReqCount === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The {reopenReqCount === 1 ? 'request' : 'requests'} behind the{' '}
            {selectedReopenable.length} selected line
            {selectedReopenable.length === 1 ? '' : 's'} will go back to{' '}
            <b>draft</b>. Their approvals are cleared and they have to be submitted
            and approved again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReopenBulk(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" disableElevation disabled={busy} onClick={reopenSelected}>
            Reopen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!confirmHandOver}
        onClose={() => setConfirmHandOver(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Hand over the whole order?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Hand-over applies to the <b>whole request</b>, not just this line —
            all{' '}
            <b>
              {(confirmHandOver &&
                confirmHandOver.request &&
                (confirmHandOver.request.lines || []).length) ||
                0}
            </b>{' '}
            lines of{' '}
            <b>
              {(confirmHandOver && confirmHandOver.po_number) || 'this order'}
            </b>{' '}
            move to handed over, and dispatch and receipt open up for each of
            them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmHandOver(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => {
              const row = confirmHandOver
              setConfirmHandOver(null)
              applyStage(row, 'handed_to_at_apartment')
            }}>
            Hand over
          </Button>
        </DialogActions>
      </Dialog>

      <RequestTracking request={trackReq} onClose={() => setTrackReq(null)} />

      <Popover
        open={!!approvalReq}
        anchorEl={approvalReq && approvalReq.anchorEl}
        onClose={() => setApprovalReq(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box style={{ padding: 16, width: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="subtitle2">
            Approval sign-off
            {approvalReq && approvalReq.bulk
              ? ` · ${approvalReq.count} request${approvalReq.count === 1 ? '' : 's'}`
              : ''}
          </Typography>
          <Box>
            <Typography variant="caption" color="textSecondary">Needs approval?</Typography>
            <Select
              value={approvalForm.approval_needed || ''}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => {
                const v = e.target.value
                setApprovalForm((p) => ({ ...p, approval_needed: v }))
              }}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </Box>
          <TextField
            size="small"
            label="Approved by"
            placeholder="e.g. Sonak"
            value={approvalForm.approved_by_name || ''}
            onChange={(e) => {
              const v = e.target.value
              setApprovalForm((p) => ({ ...p, approved_by_name: v }))
            }}
          />
          <Box>
            <Typography variant="caption" color="textSecondary">Source</Typography>
            <Select
              value={approvalForm.approval_source || ''}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => {
                const v = e.target.value
                setApprovalForm((p) => ({ ...p, approval_source: v }))
              }}>
              <MenuItem value="">—</MenuItem>
              {APPROVAL_SOURCE_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </Box>
          <TextField
            size="small"
            label="Reference"
            placeholder="Subject or issue-tracker id"
            value={approvalForm.approval_reference || ''}
            onChange={(e) => {
              const v = e.target.value
              setApprovalForm((p) => ({ ...p, approval_reference: v }))
            }}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button size="small" onClick={() => setApprovalReq(null)}>Cancel</Button>
            <Button size="small" color="primary" variant="contained" disableElevation
              disabled={busy} onClick={submitApproval}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

// ---- Bulk upload (download CSV template -> upload -> draft) ----------------
const REQUIRED_KEYS = ['item_name', 'category', 'qty', 'vendor']
const REQUIRED_LABELS = { item_name: 'Product', category: 'Category', qty: 'Units', vendor: 'Vendor' }

const BulkUploadDialog = ({
  bulk,
  setBulk,
  apartments,
  vendorNames,
  onClose,
  onCreated
}) => {
  const classes = useStyles()
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const fileRef = React.useRef(null)

  const downloadTemplate = () => {
    const header = BULK_COLS.map(([h]) => h)
    const sample = { ...BULK_SAMPLE, category: APT_CATEGORIES[0], vendor: vendorNames[0] || 'Anmol' }
    const example = BULK_COLS.map(([, k]) => sample[k] ?? '')
    const csv = Papa.unparse({ fields: header, data: [example] })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'at-apartment-bulk-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    setError('')
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const headerToKey = Object.fromEntries(BULK_COLS.map(([h, k]) => [h, k]))
        const parsed = (res.data || []).map((raw) => {
          const line = {}
          Object.entries(raw).forEach(([h, val]) => {
            const key = headerToKey[String(h).trim()]
            if (key) line[key] = String(val ?? '').trim()
          })
          return line
        })
        // Drop entirely-blank rows and the sample row.
        const cleaned = parsed.filter(
          (l) => l.item_name && !/^example item/i.test(l.item_name)
        )
        setBulk((b) => ({ ...b, rows: cleaned, fileName: file.name }))
      },
      error: (err) => setError(err.message || 'Could not read the file.')
    })
    // Allow re-picking the same file.
    e.target.value = ''
  }

  const validated = React.useMemo(() => {
    if (!bulk.rows) return { valid: [], invalid: [] }
    const valid = []
    const invalid = []
    bulk.rows.forEach((line, i) => {
      const missing = REQUIRED_KEYS.filter((k) =>
        k === 'qty' ? !(Number(line.qty) > 0) : !String(line[k] || '').trim()
      ).map((k) => REQUIRED_LABELS[k])
      const badCategory =
        line.category && !aptCategoryNames().includes(line.category)
      if (missing.length || badCategory) {
        invalid.push({
          row: i + 1,
          reason: [
            missing.length ? `missing ${missing.join(', ')}` : '',
            badCategory ? `category "${line.category}" not one of the six` : ''
          ]
            .filter(Boolean)
            .join('; ')
        })
      } else {
        valid.push(line)
      }
    })
    return { valid, invalid }
  }, [bulk.rows])

  const create = async () => {
    setBusy(true)
    setError('')
    try {
      const apt = apartments.find((a) => a.uuid === bulk.apartment)
      const created = await createAptRequest({
        apartment: bulk.apartment,
        title: `Bulk upload — ${apt ? apt.name : ''}${
          bulk.fileName ? ` (${bulk.fileName})` : ''
        }`,
        lines: validated.valid
      })
      onCreated(created)
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.detail) ||
          'Could not create the draft.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk upload request lines</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Download the template, fill one row per line, pick the apartment and
          upload. The rows land as a <b>draft</b> for that apartment — review in
          the grid, then submit for approval. (CSV — opens and saves in Excel.)
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={1} mb={1}>
          <Typography variant="body2">Apartment:</Typography>
          <Select
            value={bulk.apartment || ''}
            style={{ fontSize: 14 }}
            onChange={(e) => setBulk((b) => ({ ...b, apartment: e.target.value }))}>
            {apartments.map((a) => (
              <MenuItem key={a.uuid} value={a.uuid}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box display="flex" style={{ gap: 8 }} mb={1}>
          <Button variant="outlined" size="small" onClick={downloadTemplate}>
            Download template
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileRef.current && fileRef.current.click()}>
            Choose CSV…
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={onFile}
          />
        </Box>

        {bulk.fileName && (
          <Typography variant="body2" className={classes.bulkNote}>
            <b>{bulk.fileName}</b>: {validated.valid.length} line
            {validated.valid.length === 1 ? '' : 's'} ready
            {validated.invalid.length
              ? `, ${validated.invalid.length} skipped`
              : ''}
            .
          </Typography>
        )}
        {validated.invalid.length > 0 && (
          <Box mt={1}>
            {validated.invalid.slice(0, 8).map((r) => (
              <Typography key={r.row} variant="caption" display="block" color="error">
                Row {r.row}: {r.reason}
              </Typography>
            ))}
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          disabled={busy || !bulk.apartment || !validated.valid.length}
          onClick={create}>
          Create draft ({validated.valid.length})
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RequestsTab
